import React, { useState, useEffect, useRef, useCallback, forwardRef, useImperativeHandle } from 'react';
import Map, { Marker, Popup, Source, Layer, NavigationControl, FullscreenControl, GeolocateControl } from 'react-map-gl/mapbox';
import { 
  MAPBOX_CONFIG, 
  getMapStyleForTimeOfDay,
  getLightingPresetForTimeOfDay,
  imageToLatLng, 
  calculateRouteToGrave, 
  findNearestRoadPoint,
  optimizeMultiGraveRoute,
  findGravesWithinDistance,
  calculateRouteAlternatives,
  navigateBetweenGraves,
  planInteractiveRoute,
  RoadGraph
} from '../config/mapbox';
import ColumbariumGrid from './ColumbariumGrid';
import 'mapbox-gl/dist/mapbox-gl.css';
import './MapboxMap.css';

const MapboxMap = forwardRef(({
  lots = [],
  selectedLot = null,
  onLotClick = () => {},
  onMapClick = () => {},
  onLotCreate = () => {},
  isEditing = false,
  showRoads = true,
  showColumbarium = true,
  className = '',
  style = {},
  // Navigation props for GraveLocator integration
  onNavigationStateChange = () => {},
  onNextStep = () => {},
  onStopNavigation = () => {},
  // Bookmark props
  bookmarks = [],
  onBookmark = null,
  bookmarkLoading = false,
  // Status filter prop
  statusFilter = 'all',
  onStatusFilterChange = () => {},
  // Time of day prop
  timeOfDay = 'dawn',
  // Callback to notify parent when columbarium grid is shown/hidden
  onColumbariumGridToggle = () => {}
}, ref) => {
  const mapRef = useRef(null);
  const [viewport, setViewport] = useState({
    latitude: MAPBOX_CONFIG.defaultViewport.latitude,
    longitude: MAPBOX_CONFIG.defaultViewport.longitude,
    zoom: MAPBOX_CONFIG.defaultViewport.zoom,
    bearing: 0,
    pitch: 60
  });
  
  const [popupInfo, setPopupInfo] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [clickedPosition, setClickedPosition] = useState(null);
  const [mapError, setMapError] = useState(null);
  const [isStyleLoaded, setIsStyleLoaded] = useState(false);
  const [navigationRoute, setNavigationRoute] = useState(null);
  const [isNavigating, setIsNavigating] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [roadNetwork, setRoadNetwork] = useState(null);
  const [entranceCoords, setEntranceCoords] = useState([121.06856558002767, 14.538230619100048]);
  const [showColumbariumGrid, setShowColumbariumGrid] = useState(false);
  const [currentColumbariumFloor, setCurrentColumbariumFloor] = useState(1);

  // Notify parent when columbarium grid state changes
  useEffect(() => {
    onColumbariumGridToggle(showColumbariumGrid);
  }, [showColumbariumGrid, onColumbariumGridToggle]);
  const [routeAlternatives, setRouteAlternatives] = useState(null);
  const [hoveredTooltip, setHoveredTooltip] = useState(null);

  // Notify parent component of navigation state changes
  useEffect(() => {
    onNavigationStateChange({
      isNavigating,
      navigationRoute,
      currentStep
    });
  }, [isNavigating, navigationRoute, currentStep, onNavigationStateChange]);

  // Expose navigation functions to parent component

  useImperativeHandle(ref, () => ({
    nextStep, // Internal function - no circular calls
    stopNavigation, // Internal function - no circular calls
    navigateToGrave,
    navigateBetweenGraves: navigateBetweenGravesLocal, // Exposed point-to-point navigation
    getGardenALots: () => gardenALots,
    getGardenBLots: () => gardenBLots,
    getGardenCLots: () => gardenCLots,
    getGardenDLots: () => gardenDLots,
    getAll3DGardenLots: () => [...gardenALots, ...gardenBLots, ...gardenCLots, ...gardenDLots], // Combined 3D lots
    getFilteredGraveCount: () => getFilteredGraveCount(),
    getFilteredColumbariumCount: () => getFilteredColumbariumCount(),
    getTotalCount: () => getTotalCount()
  }));
  const [selectedRoute, setSelectedRoute] = useState(null);
  const [gardenAData, setGardenAData] = useState(null);
  const [gardenALots, setGardenALots] = useState([]);
  const [gardenBData, setGardenBData] = useState(null);
  const [gardenBLots, setGardenBLots] = useState([]);
  const [gardenCData, setGardenCData] = useState(null);
  const [gardenCLots, setGardenCLots] = useState([]);
  const [gardenDData, setGardenDData] = useState(null);
  const [gardenDLots, setGardenDLots] = useState([]);
  
  // Use dynamic map style based on time of day
  const [currentMapStyle, setCurrentMapStyle] = useState(getMapStyleForTimeOfDay(timeOfDay));
  
  // Update map style when time of day changes
  useEffect(() => {
    const newStyle = getMapStyleForTimeOfDay(timeOfDay);
    setCurrentMapStyle(newStyle);
  }, [timeOfDay]);

  // Apply lighting preset when time of day changes (for Mapbox Standard)
  useEffect(() => {
    if (mapRef.current && isStyleLoaded) {
      const map = mapRef.current.getMap();
      const lightingPreset = getLightingPresetForTimeOfDay(timeOfDay);
      
      try {
        // Define lighting configurations for each time of day
        const lightingConfigs = {
          dawn: {
            color: '#FFB366', // Warm orange
            intensity: 0.5,
            position: [1.15, 210, 30],
            anchor: 'viewport'
          },
          day: {
            color: 'white',
            intensity: 0.8,
            position: [1.15, 210, 30],
            anchor: 'viewport'
          },
          dusk: {
            color: '#FF6B6B', // Warm red-orange
            intensity: 0.4,
            position: [1.15, 210, 30],
            anchor: 'viewport'
          },
          night: {
            color: '#4A90E2', // Cool blue
            intensity: 0.2,
            position: [1.15, 210, 30],
            anchor: 'viewport'
          }
        };

        const config = lightingConfigs[timeOfDay] || lightingConfigs.day;
        
        // Apply the lighting configuration
        map.setLight(config);
        
        // Try to set the lighting preset using Mapbox Standard API
        try {
          map.setConfigProperty('basemap', 'lightPreset', lightingPreset);
        } catch (configError) {
          // If setConfigProperty doesn't work, the basic setLight should still work
          console.log('Using basic lighting configuration');
        }
        
        console.log(`Applied ${lightingPreset} lighting preset with config:`, config);
      } catch (error) {
        console.warn('Could not apply lighting preset:', error);
      }
    }
  }, [timeOfDay, isStyleLoaded]);
  
  // Status generation function with percentage-based distribution
  const getRealisticStatus = useCallback((name, row, column) => {
    // Create a consistent hash from the coordinates
    const hash = (name + row + column).split('').reduce((a, b) => {
      a = ((a << 5) - a) + b.charCodeAt(0);
      return a & a;
    }, 0);
    
    // Use percentage-based distribution
    const random = Math.abs(hash) % 100;
    
    if (random < 60) return 'available';      // 60% available
    if (random < 85) return 'occupied';       // 25% occupied  
    if (random < 95) return 'reserved';       // 10% reserved
    return 'unavailable';                     // 5% unavailable
  }, []);
  
  // Status color function
  const getStatusColor = useCallback((status) => {
    const colors = {
      available: '#22c55e',    // Green
      occupied: '#ef4444',     // Red
      reserved: '#3b82f6',     // Blue
      unavailable: '#f59e0b'   // Yellow
    };
    return colors[status] || '#22c55e';
  }, []);
  
  // Count filtered graves based on status (excluding landmarks)
  const getFilteredGraveCount = useCallback(() => {
    // Count from lots array, excluding landmarks
    const gravesOnly = lots.filter(lot => lot.type !== 'landmark');
    
    if (statusFilter === 'all') {
      return gravesOnly.length;
    }
    
    // For specific status filters, count matching graves
    let graveCount = 0;
    [...gardenAData?.features || [], ...gardenBData?.features || [], ...gardenCData?.features || [], ...gardenDData?.features || []]
      .forEach(feature => {
        if (feature.properties && feature.properties.type === 'grave') {
          const props = feature.properties;
          const row = props.row || props.grave_row;
          const column = props.column || props.grave_column;
          const status = props.status || 'available'; // Use real status from database
          if (status === statusFilter) {
            graveCount++;
          }
        }
      });
    
    return graveCount;
  }, [statusFilter, lots, gardenAData, gardenBData, gardenCData, gardenDData]);

  // Count filtered columbarium blocks based on status
  const getFilteredColumbariumCount = useCallback(() => {
    // Count columbarium blocks (8 rows × 50 columns = 400 blocks per floor)
    const columbariumBlocksPerFloor = 8 * 50; // 400 blocks per floor
    
    if (statusFilter === 'all') {
      return columbariumBlocksPerFloor;
    }
    
    // Count columbarium blocks with matching status for current floor
    // Using the same hash function as in ColumbariumGrid to ensure consistent status distribution
    const hash = (x, y, floorNum) => {
      let h = x * 374761393 + y * 668265263 + floorNum * 123456789;
      h = (h ^ (h >> 13)) * 1274126177;
      return (h ^ (h >> 16)) % 100;
    };
    
    let columbariumCount = 0;
    // Only count current floor
    const currentFloor = currentColumbariumFloor;
    for (let row = 0; row < 8; row++) {
      for (let col = 0; col < 50; col++) {
        const randomValue = hash(row, col, currentFloor);
        let status;
        
        if (randomValue < 60) status = 'available';      // 60% available
        else if (randomValue < 85) status = 'occupied';  // 25% occupied
        else if (randomValue < 95) status = 'reserved';  // 10% reserved
        else status = 'unavailable';                     // 5% unavailable
        
        if (status === statusFilter) {
          columbariumCount++;
        }
      }
    }
    
    return columbariumCount;
  }, [statusFilter, currentColumbariumFloor]);

  // Get total count (graves + columbarium blocks)
  const getTotalCount = useCallback(() => {
    return getFilteredGraveCount() + getFilteredColumbariumCount();
  }, [getFilteredGraveCount, getFilteredColumbariumCount]);

  // Load road network from your actual GeoJSON data and create road graph
  const loadRoadNetwork = useCallback(async () => {
    try {
      // Load your actual GeoJSON file
      const response = await fetch('/data/cemetery_smart_optimized_graph.json');
      if (!response.ok) {
        throw new Error(`Failed to load GeoJSON: ${response.status}`);
      }
      
      const geoJsonData = await response.json();
      
      // Extract entrance coordinates from Main Road features
      const mainRoads = (geoJsonData.features || []).filter(feature => 
        feature && feature.properties && feature.properties.name === "Main Road" && feature.properties.type === "main"
      );
      
      // Find the main road that starts with the entrance coordinates
      let entranceCoords = [121.06856558002767, 14.538230619100048]; // Default fallback
      for (const road of mainRoads) {
        if (road.geometry.coordinates && road.geometry.coordinates.length > 0) {
          const firstCoord = road.geometry.coordinates[0];
          if (firstCoord[0] === 121.068655 && firstCoord[1] === 14.538215) {
            entranceCoords = firstCoord;
            break;
          }
        }
      }
      
      // Filter all road and pathway features from your complete GeoJSON
      const roadFeatures = (geoJsonData.features || []).filter(feature => 
        feature && feature.properties && (
          feature.properties.type === 'road' || 
          feature.properties.type === 'main' || 
          feature.properties.type === 'pathway'
        )
      );
      
      const roadNetwork = {
        type: 'FeatureCollection',
        features: roadFeatures
      };
      
      // Create road graph from your actual GeoJSON data using our own implementation
      const roadGraph = buildProperRoadNetwork(geoJsonData);
      setRoadNetwork(roadGraph);
      
    } catch (error) {
      console.error('Failed to load GeoJSON road network:', error);
      
      // Use a simple mock road network structure
      const mockRoadGraph = {
        nodes: {},
        edges: {},
        roadSegments: []
      };
      setRoadNetwork(mockRoadGraph);
    }
  }, []);

  // Convert Garden A GeoJSON features to lot format
  const convertGardenAToLots = useCallback((geoJsonData) => {
    if (!geoJsonData || !geoJsonData.features) return [];
    
    return (geoJsonData.features || [])
      .filter(feature => feature && feature.properties && (feature.properties.type === 'grave' || feature.properties.type === 'niche'))
      .map(feature => {
        const props = feature.properties;
        const coords = feature.geometry.coordinates[0]; // First ring of polygon
        
        // Calculate bounds from polygon coordinates
        const lngs = coords.map(coord => coord[0]);
        const lats = coords.map(coord => coord[1]);
        const minLng = Math.min(...lngs);
        const maxLng = Math.max(...lngs);
        const minLat = Math.min(...lats);
        const maxLat = Math.max(...lats);
        
        return {
          _id: feature.id || `${props.type}_${props.name}`,
          id: props.row && props.column ? `A-${props.row}-${props.column}` : `A-${props.name}`,
          name: props.name || '',
          status: 'available', // Default status for Garden A lots
          garden: 'A',
          row: props.row || props.grave_row,
          column: props.column || props.grave_column,
          type: props.type,
          location: `Garden A, Row ${props.row || props.grave_row}, Column ${props.column || props.grave_column}`,
          bounds: [[minLat, minLng], [maxLat, maxLng]],
          coordinates: {
            type: 'Point',
            coordinates: [
              (minLng + maxLng) / 2, // center lng
              (minLat + maxLat) / 2  // center lat
            ]
          },
          sqm: props.type === 'grave' ? '2' : '1', // Estimate based on type
          price: props.type === 'grave' ? '50000' : '25000', // Default pricing
          isFromGeoJSON: true // Flag to identify Garden A lots
        };
      });
  }, []);

  // Convert Garden B GeoJSON features to lot format
  const convertGardenBToLots = useCallback((geoJsonData) => {
    if (!geoJsonData || !geoJsonData.features) return [];
    
    return (geoJsonData.features || [])
      .filter(feature => feature && feature.properties && (feature.properties.type === 'grave' || feature.properties.type === 'niche'))
      .map(feature => {
        const props = feature.properties;
        const coords = feature.geometry.coordinates[0]; // First ring of polygon
        
        // Calculate bounds from polygon coordinates
        const lngs = coords.map(coord => coord[0]);
        const lats = coords.map(coord => coord[1]);
        const minLng = Math.min(...lngs);
        const maxLng = Math.max(...lngs);
        const minLat = Math.min(...lats);
        const maxLat = Math.max(...lats);
        
        return {
          _id: feature.id || `${props.type}_${props.name}`,
          id: props.row && props.column ? `B-${props.row}-${props.column}` : `B-${props.name}`,
          name: props.name || '',
          status: 'available', // Default status for Garden B lots
          garden: 'B',
          row: props.row || props.grave_row,
          column: props.column || props.grave_column,
          type: props.type,
          location: `Garden B, Row ${props.row || props.grave_row}, Column ${props.column || props.grave_column}`,
          bounds: [[minLat, minLng], [maxLat, maxLng]],
          coordinates: {
            type: 'Point',
            coordinates: [
              (minLng + maxLng) / 2, // center lng
              (minLat + maxLat) / 2  // center lat
            ]
          },
          sqm: props.type === 'grave' ? '2' : '1', // Estimate based on type
          price: props.type === 'grave' ? '50000' : '25000', // Default pricing
          isFromGeoJSON: true // Flag to identify Garden B lots
        };
      });
  }, []);

  // Convert Garden C GeoJSON features to lot format
  const convertGardenCToLots = useCallback((geoJsonData) => {
    if (!geoJsonData || !geoJsonData.features) return [];
    
    return (geoJsonData.features || [])
      .filter(feature => feature && feature.properties && (feature.properties.type === 'grave' || feature.properties.type === 'niche'))
      .map(feature => {
        const props = feature.properties;
        const coords = feature.geometry.coordinates[0]; // First ring of polygon
        
        // Calculate bounds from polygon coordinates
        const lngs = coords.map(coord => coord[0]);
        const lats = coords.map(coord => coord[1]);
        const minLng = Math.min(...lngs);
        const maxLng = Math.max(...lngs);
        const minLat = Math.min(...lats);
        const maxLat = Math.max(...lats);
        
        return {
          _id: feature.id || `${props.type}_${props.name}`,
          id: props.row && props.column ? `C-${props.row}-${props.column}` : `C-${props.name}`,
          name: props.name || '',
          status: 'available', // Default status for Garden C lots
          garden: 'C',
          row: props.row || props.grave_row,
          column: props.column || props.grave_column,
          type: props.type,
          location: `Garden C, Row ${props.row || props.grave_row}, Column ${props.column || props.grave_column}`,
          bounds: [[minLat, minLng], [maxLat, maxLng]],
          coordinates: {
            type: 'Point',
            coordinates: [
              (minLng + maxLng) / 2, // center lng
              (minLat + maxLat) / 2  // center lat
            ]
          },
          sqm: props.type === 'grave' ? '2' : '1', // Estimate based on type
          price: props.type === 'grave' ? '50000' : '25000', // Default pricing
          isFromGeoJSON: true // Flag to identify Garden C lots
        };
      });
  }, []);

  // Convert Garden D GeoJSON features to lot format
  const convertGardenDToLots = useCallback((geoJsonData) => {
    if (!geoJsonData || !geoJsonData.features) return [];
    
    return (geoJsonData.features || [])
      .filter(feature => feature && feature.properties && (feature.properties.type === 'grave' || feature.properties.type === 'niche'))
      .map(feature => {
        const props = feature.properties;
        const coords = feature.geometry.coordinates[0]; // First ring of polygon
        
        // Calculate bounds from polygon coordinates
        const lngs = coords.map(coord => coord[0]);
        const lats = coords.map(coord => coord[1]);
        const minLng = Math.min(...lngs);
        const maxLng = Math.max(...lngs);
        const minLat = Math.min(...lats);
        const maxLat = Math.max(...lats);
        
        return {
          _id: feature.id || `${props.type}_${props.name}`,
          id: props.row && props.column ? `D-${props.row}-${props.column}` : `D-${props.name}`,
          name: props.name || '',
          status: 'available', // Default status for Garden D lots
          garden: 'D',
          row: props.row || props.grave_row,
          column: props.column || props.grave_column,
          type: props.type,
          location: `Garden D, Row ${props.row || props.grave_row}, Column ${props.column || props.grave_column}`,
          bounds: [[minLat, minLng], [maxLat, maxLng]],
          coordinates: {
            type: 'Point',
            coordinates: [
              (minLng + maxLng) / 2, // center lng
              (minLat + maxLat) / 2  // center lat
            ]
          },
          sqm: props.type === 'grave' ? '2' : '1', // Estimate based on type
          price: props.type === 'grave' ? '50000' : '25000', // Default pricing
          isFromGeoJSON: true // Flag to identify Garden D lots
        };
      });
  }, []);

  // Load Garden A data from backend API with real database status
  const loadGardenAData = useCallback(async () => {
    try {
      // Load from backend API (includes real status from database)
      const API_URL = process.env.REACT_APP_API_URL || '';
      const response = await fetch(`${API_URL}/public/garden-a`);
      if (!response.ok) {
        throw new Error(`Failed to load Garden A data: ${response.status}`);
      }
      
      const geoJsonData = await response.json();
      
      setGardenAData(geoJsonData);
      
      // Convert to lots format for internal use
      const gardenALots = convertGardenAToLots(geoJsonData);
      
      // Store Garden A lots in state for internal use
      setGardenALots(gardenALots);
      
    } catch (error) {
      console.error('Failed to load Garden A data:', error);
    }
  }, [convertGardenAToLots]);

  // Load Garden B data from backend API with real database status
  const loadGardenBData = useCallback(async () => {
    try {
      // Load from backend API (includes real status from database)
      const API_URL = process.env.REACT_APP_API_URL || '';
      const response = await fetch(`${API_URL}/public/garden-b`);
      if (!response.ok) {
        throw new Error(`Failed to load Garden B data: ${response.status}`);
      }
      
      const geoJsonData = await response.json();
      
      setGardenBData(geoJsonData);
      
      // Convert to lots format for internal use
      const gardenBLots = convertGardenBToLots(geoJsonData);
      
      // Store Garden B lots in state for internal use
      setGardenBLots(gardenBLots);
      
    } catch (error) {
      console.error('Failed to load Garden B data:', error);
    }
  }, [convertGardenBToLots]);

  // Load Garden C data from backend API with real database status
  const loadGardenCData = useCallback(async () => {
    try {
      // Load from backend API (includes real status from database)
      const API_URL = process.env.REACT_APP_API_URL || '';
      const response = await fetch(`${API_URL}/public/garden-c`);
      if (!response.ok) {
        throw new Error(`Failed to load Garden C data: ${response.status}`);
      }
      
      const geoJsonData = await response.json();
      
      setGardenCData(geoJsonData);
      
      // Convert to lots format for internal use
      const gardenCLots = convertGardenCToLots(geoJsonData);
      
      // Store Garden C lots in state for internal use
      setGardenCLots(gardenCLots);
      
    } catch (error) {
      console.error('Failed to load Garden C data:', error);
    }
  }, [convertGardenCToLots]);

  // Load Garden D data from backend API with real database status
  const loadGardenDData = useCallback(async () => {
    try {
      // Load from backend API (includes real status from database)
      const API_URL = process.env.REACT_APP_API_URL || '';
      const response = await fetch(`${API_URL}/public/garden-d`);
      if (!response.ok) {
        throw new Error(`Failed to load Garden D data: ${response.status}`);
      }
      
      const geoJsonData = await response.json();
      
      setGardenDData(geoJsonData);
      
      // Convert to lots format for internal use
      const gardenDLots = convertGardenDToLots(geoJsonData);
      
      // Store Garden D lots in state for internal use
      setGardenDLots(gardenDLots);
      
    } catch (error) {
      console.error('Failed to load Garden D data:', error);
    }
  }, [convertGardenDToLots]);

  useEffect(() => {
    loadRoadNetwork();
    loadGardenAData();
    loadGardenBData();
    loadGardenCData();
    loadGardenDData();
  }, [loadRoadNetwork, loadGardenAData, loadGardenBData, loadGardenCData, loadGardenDData]);

  // Hide road lines but keep landmarks visible
  const hideRoadLayers = (map) => {
    try {
      const style = map.getStyle();
      if (style && style.layers) {
        style.layers.forEach(layer => {
          
          // Hide garden polygons
          if (layer.id.includes('cemetery-gardens') ||
              layer.id.includes('garden') ||
              layer.id.includes('polygon') ||
              layer.id.includes('section') ||
              layer.id.includes('plot') ||
              layer.id.includes('area')) {
            map.setLayoutProperty(layer.id, 'visibility', 'none');
          }
          
          // For cemetery-roads layer, we need to filter by data properties
          if (layer.id.includes('cemetery-roads')) {
            // Set up filter to show only landmarks (type: "landmark")
            try {
              map.setFilter(layer.id, ['==', ['get', 'type'], 'landmark']);
            } catch (filterError) {
              console.warn(`Could not filter ${layer.id}:`, filterError);
              // If filtering fails, just hide the entire layer
              map.setLayoutProperty(layer.id, 'visibility', 'none');
            }
          }
          
          // Hide other road-related layers (but not cemetery-roads since we're filtering it)
          if ((layer.id.includes('road') || 
               layer.id.includes('path') || 
               layer.id.includes('way') ||
               layer.id.includes('highway') ||
               layer.id.includes('street') ||
               layer.id.includes('line')) && 
               !layer.id.includes('cemetery-roads')) {
            map.setLayoutProperty(layer.id, 'visibility', 'none');
          }
        });
      }
    } catch (error) {
      console.warn('Could not hide road layers:', error);
    }
  };

  // Navigation functions
  const startNavigation = (fromLandmark, toLandmark) => {
    const route = calculateCemeteryRoute(fromLandmark, toLandmark);
    setNavigationRoute(route);
    setIsNavigating(true);
    setCurrentStep(0);
    
    // Center map on route
    if (mapRef.current && route) {
      mapRef.current.flyTo({
        center: route.waypoints[0],
        zoom: 21,
        bearing: 90, // Rotate to face east
        pitch: 60,   // Maintain 3D view angle
        duration: 1000
      });
    }
  };

  const stopNavigation = () => {
    setNavigationRoute(null);
    setIsNavigating(false);
    setCurrentStep(0);
  };

  const nextStep = () => {
    if (navigationRoute && currentStep < navigationRoute.instructions.length - 1) {
      const newStep = currentStep + 1;
      setCurrentStep(newStep);
      
      // Zoom to the current instruction location with proper timing
      setTimeout(() => {
        if (mapRef.current && navigationRoute.waypoints && navigationRoute.waypoints.length > newStep) {
          const currentInstructionCoords = navigationRoute.waypoints[newStep];
          // console.log('Next step zooming to:', currentInstructionCoords, 'step:', newStep);
          mapRef.current.flyTo({
            center: currentInstructionCoords,
            zoom: 20,
            bearing: 90, // Maintain eastward orientation
            pitch: 60,   // Maintain 3D view angle
            duration: 800
          });
        }
      }, 100); // Small delay to ensure state is updated
    } else {
      stopNavigation();
    }
  };

  const calculateCemeteryRoute = (from, to) => {
    // Simple route calculation between cemetery landmarks
    const landmarks = MAPBOX_CONFIG.internalNavigation.landmarks;
    const fromLandmark = landmarks.find(l => l.name === from);
    const toLandmark = landmarks.find(l => l.name === to);
    
    if (!fromLandmark || !toLandmark) return null;

    // Create waypoints between landmarks
    const waypoints = [
      fromLandmark.coordinates,
      toLandmark.coordinates
    ];

    // Generate turn-by-turn instructions
    const instructions = [
      {
        type: 'start',
        instruction: `Start at ${fromLandmark.name}`,
        distance: 0,
        icon: '📍'
      },
      {
        type: 'turn',
        instruction: `Head towards ${toLandmark.name}`,
        distance: calculateDistance(fromLandmark.coordinates, toLandmark.coordinates),
        icon: '➡️'
      },
      {
        type: 'arrive',
        instruction: `Arrive at ${toLandmark.name}`,
        distance: 0,
        icon: '🎯'
      }
    ];

    return {
      waypoints,
      instructions,
      totalDistance: calculateDistance(fromLandmark.coordinates, toLandmark.coordinates),
      estimatedTime: Math.ceil(calculateDistance(fromLandmark.coordinates, toLandmark.coordinates) / 60) // 60 meters per minute (1 m/s walking speed)
    };
  };

  const calculateDistance = (coord1, coord2) => {
    // Validate input coordinates
    if (!coord1 || !coord2 || !Array.isArray(coord1) || !Array.isArray(coord2) ||
        coord1.length < 2 || coord2.length < 2 ||
        typeof coord1[0] !== 'number' || typeof coord1[1] !== 'number' ||
        typeof coord2[0] !== 'number' || typeof coord2[1] !== 'number') {
      console.warn('Invalid coordinates for distance calculation:', { coord1, coord2 });
      return Infinity; // Return large distance for invalid coordinates
    }
    
    // Simple distance calculation (in meters)
    const R = 6371e3; // Earth's radius in meters
    const φ1 = coord1[1] * Math.PI/180;
    const φ2 = coord2[1] * Math.PI/180;
    const Δφ = (coord2[1]-coord1[1]) * Math.PI/180;
    const Δλ = (coord2[0]-coord1[0]) * Math.PI/180;

    const a = Math.sin(Δφ/2) * Math.sin(Δφ/2) +
              Math.cos(φ1) * Math.cos(φ2) *
              Math.sin(Δλ/2) * Math.sin(Δλ/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));

    return R * c;
  };

  // Enhanced navigate to specific grave using proper road network pathfinding
  const navigateToGrave = (targetGrave) => {
    
    let centerLat, centerLng;
    
    // Handle Garden A lots (from GeoJSON conversion)
    if (targetGrave.garden === 'A' && targetGrave.isFromGeoJSON) {
      // Use the converted lot data directly
      if (targetGrave.coordinates && targetGrave.coordinates.coordinates) {
        centerLng = targetGrave.coordinates.coordinates[0];
        centerLat = targetGrave.coordinates.coordinates[1];
      } else if (targetGrave.bounds) {
        centerLat = (targetGrave.bounds[0][0] + targetGrave.bounds[1][0]) / 2;
        centerLng = (targetGrave.bounds[0][1] + targetGrave.bounds[1][1]) / 2;
      } else {
        console.error('Garden A lot missing coordinates:', targetGrave);
        alert('Could not locate Garden A lot. Please try again.');
        return;
      }
    } else if (targetGrave.bounds) {
      // Handle regular graves (Gardens B, C, etc.)
      centerLat = (targetGrave.bounds[0][0] + targetGrave.bounds[1][0]) / 2;
      centerLng = (targetGrave.bounds[0][1] + targetGrave.bounds[1][1]) / 2;
    } else {
      console.error('Invalid grave data for navigation:', targetGrave);
      alert('Invalid grave data. Please try again.');
      return;
    }
    
    
    // Use entrance coordinates
    const startCoords = [121.06856558002767, 14.538230619100048]; // Entrance coordinates
    
    // Create a route that follows the road network
    createRouteFollowingRoads(startCoords, [centerLng, centerLat], targetGrave).then(route => {
      
      // Set navigation state
      setRouteAlternatives({
        direct: route,
        viaChapel: route,
        viaOffice: route
      });
      setSelectedRoute(route);
    setNavigationRoute(route);
    setIsNavigating(true);
    setCurrentStep(0);
      
      // Zoom to entrance (first instruction) when navigation starts
      setTimeout(() => {
        if (mapRef.current && route.waypoints && route.waypoints.length > 0) {
          const entranceCoords = route.waypoints[0];
          console.log('Starting navigation - zooming to entrance:', entranceCoords);
          mapRef.current.flyTo({
            center: entranceCoords,
            zoom: 20,
            bearing: 90, // Rotate to face east (90 degrees)
            pitch: 60,   // Maintain 3D view angle
            duration: 1000
          });
        }
      }, 200); // Small delay to ensure navigation state is set
      
    }).catch(error => {
      console.error('=== ROUTE GENERATION ERROR ===');
      console.error('Error creating route:', error);
      alert('Failed to create route. Please try again.');
    });
    
    // Don't immediately zoom to grave - let navigation handle the zooming
    // The navigation will start from entrance and guide user to the grave
  };

  // PROPER ROAD NETWORK ROUTING - Uses full GeoJSON road network with pathfinding
  const createRouteFollowingRoads = async (start, end, grave) => {
    // console.log('=== ROUTE CREATION DEBUG START ===');
    // console.log('Start coordinates:', start);
    // console.log('End coordinates:', end);
    // console.log('Target grave ID:', grave.id);
    
    try {
      // Load the optimized graph data
      // console.log('Loading optimized graph data...');
        const response = await fetch('/data/cemetery_smart_optimized_graph.json');
      const graphData = await response.json();
      
      console.log('Optimized graph loaded successfully:', {
        totalNodes: graphData.nodes.length,
        totalEdges: graphData.edges.length,
        connectivity: graphData.pathfinding_metadata.connectivity_analysis
      });
      
      // Use optimized graph-based pathfinding
      console.log('Using optimized graph-based A* pathfinding...');
      
      // Build graph from optimized structure
      const graph = buildGraphFromOptimizedData(graphData);
      console.log('Graph built:', {
        totalNodes: Object.keys(graph.nodes).length,
        totalEdges: Object.values(graph.edges).reduce((sum, edgeList) => sum + edgeList.length, 0) / 2
      });
      
      // Find nearest road points using optimized graph
      const startRoadPoint = findNearestRoadPointOptimized(start, graphData);
      const endRoadPoint = findNearestRoadPointOptimized(end, graphData);
      
      console.log('Road access points:', {
        start: { point: startRoadPoint.point, distance: startRoadPoint.distance, road: startRoadPoint.roadName },
        end: { point: endRoadPoint.point, distance: endRoadPoint.distance, road: endRoadPoint.roadName }
      });
      
    // Find nearest graph nodes for A* pathfinding (must be exact nodes)
    const startNode = findNearestGraphNode(startRoadPoint.point, graph.nodes);
    
    // Use the nearest existing graph node to the grave (no virtual nodes)
    // This ensures we follow the actual road network
    const endNode = findNearestGraphNode(end, graph.nodes);
      
      console.log('Graph nodes for pathfinding:', {
        startNode: startNode.id,
        endNode: endNode.id,
        startNodeCoords: startNode.coordinates,
        endNodeCoords: endNode.coordinates
      });
      
      // Debug: Check distance between end node and perpendicular point
      const distanceFromEndNodeToPerpendicular = calculateDistance(endNode.coordinates, endRoadPoint.point);
      console.log('Distance from end graph node to perpendicular point:', distanceFromEndNodeToPerpendicular.toFixed(1), 'meters');
      console.log('End graph node coordinates:', endNode.coordinates);
      console.log('Perpendicular point coordinates:', endRoadPoint.point);
      
      // Use the optimized graph-based pathfinding for shortest path
      let route = findGraphPath(startNode.coordinates, endNode.coordinates, graph, endRoadPoint, end);
      
      // If proper pathfinding fails, fall back to simple pathfinding
      if (!route || route.length < 2) {
        console.log('Proper pathfinding failed, falling back to simple pathfinding...');
        
        // Use optimized graph edges for fallback pathfinding
        const roadFeatures = graphData.edges.map(edge => {
          const sourceNode = graphData.nodes.find(n => n.id === edge.source);
          const targetNode = graphData.nodes.find(n => n.id === edge.target);
          
          if (!sourceNode || !targetNode) return null;
          
          return {
            geometry: {
              type: 'LineString',
              coordinates: [sourceNode.coordinates, targetNode.coordinates]
            },
            properties: {
              type: edge.type,
              name: edge.name,
              id: edge.id
            }
          };
        }).filter(Boolean);
        
        console.log('Using optimized graph edges for fallback:', roadFeatures.length, 'edges');
        console.log('Edge names:', roadFeatures.map(f => f.properties?.name || f.properties?.id));
        
        route = createSimpleRoadRoute(start, end, roadFeatures);
        
        // If simple road route also fails, create a minimal road-based path
        if (!route || route.length < 2) {
          console.log('Simple road route also failed, creating minimal road-based path...');
          route = [start];
          
          // Add the road access points if they exist
          if (startRoadPoint && startRoadPoint.point) {
            const startToRoadDistance = calculateDistance(start, startRoadPoint.point);
            if (startToRoadDistance > 0.0001) {
              route.push(startRoadPoint.point);
            }
          }
          
          if (endRoadPoint && endRoadPoint.point) {
            const endToRoadDistance = calculateDistance(end, endRoadPoint.point);
            if (endToRoadDistance > 0.0001) {
              route.push(endRoadPoint.point);
            }
          }
          
          route.push(end);
          console.log('Minimal road-based path created with', route.length, 'points');
        }
      }
      
      console.log('Pathfinding result:', {
        routeLength: route.length,
        routePoints: route.slice(0, 3), // Show first 3 points
        isDirectRoute: route.length === 2
      });
      
      // Calculate distance
      let totalDistance = 0;
      for (let i = 0; i < route.length - 1; i++) {
        totalDistance += calculateDistance(route[i], route[i + 1]);
      }
      
      // Generate enhanced turn-by-turn instructions using road network
      const instructions = generateTurnByTurnInstructions(route, roadNetwork);
      
      // Update the arrival instruction with the specific grave ID
      if (instructions.length > 0) {
        const lastInstruction = instructions[instructions.length - 1];
        if (lastInstruction.type === 'arrive') {
          lastInstruction.instruction = `Arrive at ${grave.id}`;
        }
      }
      
      const result = {
        waypoints: route,
        totalDistance: totalDistance,
        estimatedTime: Math.ceil(totalDistance / 60), // 60 meters per minute (1 m/s walking speed)
        roadInfo: ['Following actual road network'],
        instructions: instructions
      };
      
      console.log('=== ROUTE CREATION RESULT ===');
      console.log('Final route:', result);
      console.log('Waypoint coordinates:', result.waypoints);
      console.log('Waypoint distances:', result.waypoints.map((point, i) => 
        i > 0 ? calculateDistance(result.waypoints[i-1], point) : 0
      ));
      console.log('=== ROUTE CREATION DEBUG END ===');
      
      return result;
    } catch (error) {
      console.error('=== ROUTE CREATION ERROR ===');
      console.error('Error in route creation:', error);
      
      // Fallback to direct route
      const fallbackRoute = {
        waypoints: [start, end],
        totalDistance: calculateDistance(start, end),
        estimatedTime: Math.ceil(calculateDistance(start, end) / 60), // 60 meters per minute (1 m/s walking speed)
        roadInfo: ['Direct route (GeoJSON failed)'],
        instructions: [`Walk to ${grave.id}`]
      };
      
      console.log('Using fallback direct route:', fallbackRoute);
      return fallbackRoute;
    }
  };

  // SIMPLE ROAD ROUTE - Directly follows GeoJSON road coordinates
  const createSimpleRoadRoute = (start, end, roadFeatures) => {
    console.log('Creating simple road route from', roadFeatures.length, 'road features');
    
    // Validate inputs
    if (!Array.isArray(roadFeatures) || roadFeatures.length === 0) {
      console.warn('No valid road features provided, creating minimal road-based path');
      // Instead of direct route, create a minimal path that at least tries to follow roads
      return [start, end];
    }
    
    const route = [start];
    
    // Find the road segment closest to start
    let closestStartRoad = null;
    let closestStartPoint = null;
    let minStartDistance = Infinity;
    
    roadFeatures.forEach((feature, featureIndex) => {
      if (feature && feature.geometry && feature.geometry.coordinates) {
        feature.geometry.coordinates.forEach((coord, coordIndex) => {
          // Validate coordinate before using it
          if (Array.isArray(coord) && coord.length >= 2 && 
              typeof coord[0] === 'number' && typeof coord[1] === 'number') {
            try {
      const distance = calculateDistance(start, coord);
      if (distance < minStartDistance) {
        minStartDistance = distance;
                closestStartRoad = feature;
                closestStartPoint = coord;
              }
            } catch (error) {
              console.warn('Error calculating distance to start point:', error, coord);
            }
          } else {
            console.warn('Invalid coordinate in road feature:', feature.properties?.name || feature.id, 'coord', coordIndex, coord);
          }
        });
      }
    });
    
    // Find the road segment closest to end
    let closestEndRoad = null;
    let closestEndPoint = null;
    let minEndDistance = Infinity;
    
    roadFeatures.forEach((feature, featureIndex) => {
      if (feature && feature.geometry && feature.geometry.coordinates) {
        feature.geometry.coordinates.forEach((coord, coordIndex) => {
          // Validate coordinate before using it
          if (Array.isArray(coord) && coord.length >= 2 && 
              typeof coord[0] === 'number' && typeof coord[1] === 'number') {
            try {
      const distance = calculateDistance(end, coord);
      if (distance < minEndDistance) {
        minEndDistance = distance;
                closestEndRoad = feature;
                closestEndPoint = coord;
              }
            } catch (error) {
              console.warn('Error calculating distance to end point:', error, coord);
            }
          } else {
            console.warn('Invalid coordinate in road feature:', feature.properties?.name || feature.id, 'coord', coordIndex, coord);
          }
        });
      }
    });
    
    console.log('Closest roads:', {
      startRoad: closestStartRoad?.properties?.name || 'Unknown',
      endRoad: closestEndRoad?.properties?.name || 'Unknown',
      startDistance: minStartDistance,
      endDistance: minEndDistance,
      startPoint: start,
      closestStartPoint: closestStartPoint,
      endPoint: end,
      closestEndPoint: closestEndPoint
    });
    
    // Add closest road points (only if they're different from start/end)
    if (closestStartPoint && minStartDistance > 0.0001) {
      route.push(closestStartPoint);
    }
    
    // If different roads, add intermediate points
    if (closestStartRoad !== closestEndRoad) {
      // Find intermediate road points
      const intermediatePoints = findIntermediateRoadPointsSimple(closestStartPoint || start, closestEndPoint || end, roadFeatures);
      route.push(...intermediatePoints);
    }
    
    if (closestEndPoint && minEndDistance > 0.0001) {
      route.push(closestEndPoint);
    }
    
    route.push(end);
    
    console.log('Simple road route created with', route.length, 'points');
    return route;
  };

  // Find intermediate road points (simplified)
  const findIntermediateRoadPointsSimple = (startPoint, endPoint, roadFeatures) => {
    const intermediatePoints = [];
    
    console.log('Finding intermediate points between:', startPoint, 'and', endPoint);
    
    // Validate input parameters
    if (!startPoint || !endPoint || !Array.isArray(startPoint) || !Array.isArray(endPoint)) {
      console.warn('Invalid start or end point:', { startPoint, endPoint });
      return [];
    }
    
    if (!Array.isArray(roadFeatures)) {
      console.warn('Invalid road features:', roadFeatures);
      return [];
    }
    
    // Find roads that are roughly between start and end
    roadFeatures.forEach((feature, featureIndex) => {
      if (feature && feature.geometry && feature.geometry.coordinates) {
        const coords = feature.geometry.coordinates;
        const roadName = feature.properties?.name || `Road-${featureIndex}`;
        
        // Check each coordinate in this road
        coords.forEach((coord, coordIndex) => {
          // Validate coordinate structure
          if (!coord || !Array.isArray(coord) || coord.length < 2 || 
              typeof coord[0] !== 'number' || typeof coord[1] !== 'number') {
            console.warn('Invalid coordinate at feature', featureIndex, 'coord', coordIndex, ':', coord);
            return;
          }
          
          try {
            const startToCoord = calculateDistance(startPoint, coord);
            const coordToEnd = calculateDistance(coord, endPoint);
            const startToEnd = calculateDistance(startPoint, endPoint);
            
            // If this coordinate is roughly on the path and not too close to start/end
            if (startToCoord + coordToEnd < startToEnd * 1.5 && 
                startToCoord > 5 && coordToEnd > 5) {
              intermediatePoints.push({
                coord: coord,
                roadName: roadName,
                distanceFromStart: startToCoord
              });
            }
          } catch (error) {
            console.warn('Error calculating distance for coord:', coord, error);
          }
        });
      }
    });
    
    // Sort by distance from start
    intermediatePoints.sort((a, b) => a.distanceFromStart - b.distanceFromStart);
    
    // Remove duplicates (coordinates that are too close)
    const uniquePoints = [];
    intermediatePoints.forEach(point => {
      const isDuplicate = uniquePoints.some(existing => 
        calculateDistance(existing.coord, point.coord) < 5
      );
      if (!isDuplicate) {
        uniquePoints.push(point.coord);
      }
    });
    
    console.log('Found', uniquePoints.length, 'intermediate points');
    return uniquePoints.slice(0, 6);
  };

  // DEPRECATED: Old simplified routing function - replaced with proper road network routing
  // This function is kept for reference but no longer used

  // Build route using your actual GeoJSON road network
  const buildRouteFromRealRoads = (start, end, roadFeatures) => {
    console.log('Building route from REAL road features:', roadFeatures.length);
    
    // Convert road features to simple road segments
    const roadSegments = [];
    roadFeatures.forEach((feature, featureIndex) => {
      if (feature.coordinates && feature.coordinates.length > 0) {
        // Each road feature is a LineString with multiple coordinates
        const coords = feature.coordinates;
        for (let i = 0; i < coords.length - 1; i++) {
          roadSegments.push({
            id: `${featureIndex}-${i}`,
            name: feature.name,
            type: feature.type,
            start: coords[i],
            end: coords[i + 1]
          });
        }
      }
    });
    
    console.log('Created', roadSegments.length, 'road segments from GeoJSON');
    
    // Find nearest road points for start and end
    const startRoadPoint = findNearestRoadPointReal(start, roadSegments);
    const endRoadPoint = findNearestRoadPointReal(end, roadSegments);
    
    console.log('Start road point:', startRoadPoint);
    console.log('End road point:', endRoadPoint);
    
    // Build path through road network
    const route = [start];
    
    if (startRoadPoint) {
      route.push(startRoadPoint.point);
      
      // Find path through road segments
      const roadPath = findPathThroughRealRoads(startRoadPoint, endRoadPoint, roadSegments);
      route.push(...roadPath);
    }
    
    if (endRoadPoint) {
      route.push(endRoadPoint.point);
    }
    
    route.push(end);
    
    console.log('Built route with', route.length, 'points from REAL roads');
    return route;
  };

  // Find nearest road point using real road segments
  const findNearestRoadPointReal = (point, roadSegments) => {
    let nearestPoint = null;
    let minDistance = Infinity;
    let roadIndex = null;

    roadSegments.forEach((road, index) => {
      const pointOnRoad = findClosestPointOnLineReal(road.start, road.end, point);
      const distance = calculateDistance(point, pointOnRoad);

      if (distance < minDistance) {
        minDistance = distance;
        nearestPoint = pointOnRoad;
        roadIndex = index;
      }
    });

    return { point: nearestPoint, roadIndex, distance: minDistance };
  };

  // Find closest point on line segment
  const findClosestPointOnLineReal = (lineStart, lineEnd, targetPoint) => {
    const lineVector = [
      lineEnd[0] - lineStart[0],
      lineEnd[1] - lineStart[1]
    ];
    const pointVector = [
      targetPoint[0] - lineStart[0],
      targetPoint[1] - lineStart[1]
    ];

    const lengthSquared = lineVector[0] * lineVector[0] + lineVector[1] * lineVector[1];
    if (lengthSquared === 0) return lineStart; // Degenerate line
    
    const t = Math.max(0, Math.min(1, (
      pointVector[0] * lineVector[0] + pointVector[1] * lineVector[1]
    ) / lengthSquared));

    return [
      lineStart[0] + t * lineVector[0],
      lineStart[1] + t * lineVector[1]
    ];
  };

  // Check if two road segments are connected
  const areRoadsConnectedReal = (road1, road2) => {
    const start1 = road1.start;
    const end1 = road1.end;
    const start2 = road2.start;
    const end2 = road2.end;

    // Check if any endpoints are close to the other road segment
    const point1OnRoad2 = findClosestPointOnLineReal(start2, end2, start1);
    const point2OnRoad1 = findClosestPointOnLineReal(start1, end1, start2);
    const point3OnRoad2 = findClosestPointOnLineReal(start2, end2, end1);
    const point4OnRoad1 = findClosestPointOnLineReal(start1, end1, end2);

    // Calculate distances
    const distance1 = calculateDistance(start1, point1OnRoad2);
    const distance2 = calculateDistance(start2, point2OnRoad1);
    const distance3 = calculateDistance(end1, point3OnRoad2);
    const distance4 = calculateDistance(end2, point4OnRoad1);

    // Use a threshold for connectivity (10 meters)
    const threshold = 0.0001;
    return Math.min(distance1, distance2, distance3, distance4) < threshold;
  };

  // Find path through real road segments
  const findPathThroughRealRoads = (startPoint, endPoint, roadSegments) => {
    if (!startPoint || !endPoint) return [];
    
    let path = [];
    let currentRoadIndex = startPoint.roadIndex;
    let targetRoadIndex = endPoint.roadIndex;

    // If we're on the same road, we're done
    if (currentRoadIndex === targetRoadIndex) {
      return [];
    }

    // Find connected roads that lead to the target
    let visited = new Set([currentRoadIndex]);
    let queue = [{
      roadIndex: currentRoadIndex,
      path: []
    }];

    while (queue.length > 0) {
      const { roadIndex, path: currentPath } = queue.shift();

      // Check all roads for connections
      roadSegments.forEach((_, index) => {
        if (visited.has(index)) return;

        if (areRoadsConnectedReal(roadSegments[roadIndex], roadSegments[index])) {
          const newPath = [...currentPath];
          
          // Find the connection point between the roads
          const point1OnRoad2 = findClosestPointOnLineReal(roadSegments[index].start, roadSegments[index].end, roadSegments[roadIndex].start);
          const point2OnRoad1 = findClosestPointOnLineReal(roadSegments[roadIndex].start, roadSegments[roadIndex].end, roadSegments[index].start);
          
          // Use the point that's closest to both roads
          const distance1 = calculateDistance(roadSegments[roadIndex].start, point1OnRoad2);
          const distance2 = calculateDistance(roadSegments[index].start, point2OnRoad1);
          
          const connectionPoint = distance1 < distance2 ? point1OnRoad2 : point2OnRoad1;
          newPath.push(connectionPoint);

          if (index === targetRoadIndex) {
            // We found a path to the target road
            path = newPath;
            return;
          }

          queue.push({
            roadIndex: index,
            path: newPath
          });
          visited.add(index);
        }
      });
    }

    console.log('Found path through REAL roads with', path.length, 'connection points');
    return path;
  };

  // EXACT COPY of your working Leaflet pathfinding functions
  const createPathToPlotLeafletStyle = (start, end, roads) => {
    console.log('Using EXACT Leaflet pathfinding logic');
    
    // Find nearest road points for start and end
    const startRoadPoint = findNearestRoadPointLeafletStyle(start, roads);
    const endRoadPoint = findNearestRoadPointLeafletStyle(end, roads);
    
    console.log('Start road point:', startRoadPoint);
    console.log('End road point:', endRoadPoint);
    
    // Start path from start point
    let path = [start];
    path.push(startRoadPoint.point);
    
    // Find a path through the road network from start to end
    const roadPath = findPathThroughRoadsLeafletStyle(startRoadPoint, endRoadPoint, roads);
    path = [...path, ...roadPath];
    
    // Add the final approach to the end point
    path.push(endRoadPoint.point);
    path.push(end);
    
    console.log('Leaflet-style path created with', path.length, 'points');
    return path;
  };

  // EXACT COPY of findNearestRoadPoint from your working Leaflet version
  const findNearestRoadPointLeafletStyle = (point, roads) => {
    let nearestPoint = null;
    let minDistance = Infinity;
    let roadIndex = null;

    roads.forEach((road, index) => {
      const pointOnRoad = findClosestPointOnLineLeafletStyle(road[0], road[1], point);
      const distance = calculateDistance(point, pointOnRoad);

      if (distance < minDistance) {
        minDistance = distance;
        nearestPoint = pointOnRoad;
        roadIndex = index;
      }
    });

    return { point: nearestPoint, roadIndex, distance: minDistance };
  };

  // EXACT COPY of findClosestPointOnLine from your working Leaflet version
  const findClosestPointOnLineLeafletStyle = (lineStart, lineEnd, targetPoint) => {
    const lineVector = [
      lineEnd[0] - lineStart[0],
      lineEnd[1] - lineStart[1]
    ];
    const pointVector = [
      targetPoint[0] - lineStart[0],
      targetPoint[1] - lineStart[1]
    ];

    const lengthSquared = lineVector[0] * lineVector[0] + lineVector[1] * lineVector[1];
    const t = Math.max(0, Math.min(1, (
      pointVector[0] * lineVector[0] + pointVector[1] * lineVector[1]
    ) / lengthSquared));

    return [
      lineStart[0] + t * lineVector[0],
      lineStart[1] + t * lineVector[1]
    ];
  };

  // EXACT COPY of areRoadsConnected from your working Leaflet version
  const areRoadsConnectedLeafletStyle = (road1, road2) => {
    const start1 = road1[0];
    const end1 = road1[1];
    const start2 = road2[0];
    const end2 = road2[1];

    // Check if any endpoints are close to the other road segment
    const point1OnRoad2 = findClosestPointOnLineLeafletStyle(start2, end2, start1);
    const point2OnRoad1 = findClosestPointOnLineLeafletStyle(start1, end1, start2);
    const point3OnRoad2 = findClosestPointOnLineLeafletStyle(start2, end2, end1);
    const point4OnRoad1 = findClosestPointOnLineLeafletStyle(start1, end1, end2);

    // Calculate distances between endpoints and their closest points on the other road
    const distance1 = calculateDistance(start1, point1OnRoad2);
    const distance2 = calculateDistance(start2, point2OnRoad1);
    const distance3 = calculateDistance(end1, point3OnRoad2);
    const distance4 = calculateDistance(end2, point4OnRoad1);

    // Use a threshold for connectivity (same as Leaflet)
    const threshold = 0.0001; // ~10 meters
    return Math.min(distance1, distance2, distance3, distance4) < threshold;
  };

  // EXACT COPY of findPathThroughRoads from your working Leaflet version
  const findPathThroughRoadsLeafletStyle = (startPoint, endPoint, roads) => {
    let path = [];
    let currentRoadIndex = startPoint.roadIndex;
    let targetRoadIndex = endPoint.roadIndex;

    // If we're on the same road, we're done
    if (currentRoadIndex === targetRoadIndex) {
      return [];
    }

    // Find connected roads that lead to the target
    let visited = new Set([currentRoadIndex]);
    let queue = [{
      roadIndex: currentRoadIndex,
      path: []
    }];

    while (queue.length > 0) {
      const { roadIndex, path: currentPath } = queue.shift();

      // Check all roads for connections
      roads.forEach((_, index) => {
        if (visited.has(index)) return;

        if (areRoadsConnectedLeafletStyle(roads[roadIndex], roads[index])) {
          const newPath = [...currentPath];
          
          // Find the connection point between the roads
          const point1OnRoad2 = findClosestPointOnLineLeafletStyle(roads[index][0], roads[index][1], roads[roadIndex][0]);
          const point2OnRoad1 = findClosestPointOnLineLeafletStyle(roads[roadIndex][0], roads[roadIndex][1], roads[index][0]);
          
          // Use the point that's closest to both roads
          const distance1 = calculateDistance(roads[roadIndex][0], point1OnRoad2);
          const distance2 = calculateDistance(roads[index][0], point2OnRoad1);
          
          const connectionPoint = distance1 < distance2 ? point1OnRoad2 : point2OnRoad1;
          newPath.push(connectionPoint);

          if (index === targetRoadIndex) {
            // We found a path to the target road
            path = newPath;
            return;
          }

          queue.push({
            roadIndex: index,
            path: newPath
          });
          visited.add(index);
        }
      });
    }

    console.log('Found path through roads with', path.length, 'connection points');
    return path;
  };

  // Build route by following actual road segments from your GeoJSON
  const buildRouteFollowingActualRoads = (start, end, roadFeatures) => {
    console.log('Building route following actual road segments...');
    
    // Find the road segments that connect start to end
    const route = [start];
    
    // Find the road segment closest to start
    let currentRoad = findClosestRoadSegment(start, roadFeatures);
    let currentPosition = start;
    
    console.log('Starting from road:', currentRoad?.name);
    
    // Follow road segments until we reach the end
    const maxSteps = 20; // Prevent infinite loops
    let steps = 0;
    
    while (currentRoad && steps < maxSteps) {
      steps++;
      
      // Find the best path along this road segment
      const roadPath = getPathAlongRoadSegment(currentPosition, end, currentRoad);
      route.push(...roadPath);
      
      // Update current position to end of this road segment
      if (roadPath.length > 0) {
        currentPosition = roadPath[roadPath.length - 1];
      }
      
      // Check if we're close enough to the end
      if (calculateDistance(currentPosition, end) < 0.0001) {
        console.log('Reached destination after', steps, 'steps');
        break;
      }
      
      // Find the next road segment
      const nextRoad = findNextRoadSegment(currentPosition, end, roadFeatures, currentRoad);
      if (!nextRoad || nextRoad === currentRoad) {
        console.log('No more road segments to follow');
        break;
      }
      
      currentRoad = nextRoad;
      console.log('Moving to next road:', currentRoad.name);
    }
    
    // Add the end point
    route.push(end);
    
    // Remove duplicates
    const uniqueRoute = [];
    route.forEach(point => {
      if (uniqueRoute.length === 0 || 
          calculateDistance(point, uniqueRoute[uniqueRoute.length - 1]) > 0.00001) {
        uniqueRoute.push(point);
      }
    });
    
    console.log('Final route has', uniqueRoute.length, 'points following actual roads');
    return uniqueRoute;
  };

  // Find the road segment closest to a point
  const findClosestRoadSegment = (point, roadFeatures) => {
    let closestRoad = null;
    let minDistance = Infinity;
    
    roadFeatures.forEach(road => {
      road.coordinates.forEach(coord => {
        const distance = calculateDistance(point, coord);
        if (distance < minDistance) {
          minDistance = distance;
          closestRoad = road;
        }
      });
    });
    
    return closestRoad;
  };

  // Get path along a road segment
  const getPathAlongRoadSegment = (start, end, road) => {
    const path = [];
    
    // Find the closest point on this road to start
    let startIndex = 0;
    let minStartDistance = Infinity;
    
    road.coordinates.forEach((coord, index) => {
      const distance = calculateDistance(start, coord);
      if (distance < minStartDistance) {
        minStartDistance = distance;
        startIndex = index;
      }
    });
    
    // Find the closest point on this road to end
    let endIndex = road.coordinates.length - 1;
    let minEndDistance = Infinity;
    
    road.coordinates.forEach((coord, index) => {
      const distance = calculateDistance(end, coord);
      if (distance < minEndDistance) {
        minEndDistance = distance;
        endIndex = index;
      }
    });
    
    // Add coordinates between start and end indices
    const startIdx = Math.min(startIndex, endIndex);
    const endIdx = Math.max(startIndex, endIndex);
    
    for (let i = startIdx; i <= endIdx; i++) {
      path.push(road.coordinates[i]);
    }
    
    return path;
  };

  // Find the next road segment to follow
  const findNextRoadSegment = (currentPos, end, roadFeatures, currentRoad) => {
    let bestRoad = null;
    let bestScore = Infinity;
    
    roadFeatures.forEach(road => {
      if (road === currentRoad) return; // Skip current road
      
      // Check if this road is connected to current position
      const isConnected = road.coordinates.some(coord => 
        calculateDistance(currentPos, coord) < 0.0001
      );
      
      if (isConnected) {
        // Calculate how much closer this road gets us to the end
        const roadEnd = road.coordinates[road.coordinates.length - 1];
        const distanceToEnd = calculateDistance(roadEnd, end);
        const currentDistanceToEnd = calculateDistance(currentPos, end);
        
        const score = distanceToEnd; // Lower is better
        
        if (score < bestScore) {
          bestScore = score;
          bestRoad = road;
        }
      }
    });
    
    return bestRoad;
  };

  // Build route directly from road points (simple and reliable)
  const buildRouteFromRoadPoints = (start, end, roadCoords) => {
    console.log('Building route from', roadCoords.length, 'road coordinates');
    
    // Find closest road points to start and end
    const startRoadPoint = findClosestRoadPoint(start, roadCoords);
    const endRoadPoint = findClosestRoadPoint(end, roadCoords);
    
    console.log('Start road point:', startRoadPoint);
    console.log('End road point:', endRoadPoint);
    
    // Build route by finding intermediate road points
    const route = [start];
    
    // Add start road point
    if (startRoadPoint) {
      route.push(startRoadPoint);
    }
    
    // Find intermediate road points between start and end (simplified)
    // const intermediatePoints = findIntermediateRoadPoints(startRoadPoint, endRoadPoint, roadCoords);
    // route.push(...intermediatePoints);
    
    // Add end road point
    if (endRoadPoint) {
      route.push(endRoadPoint);
    }
    
    // Add end point
    route.push(end);
    
    // Remove duplicates and return
    const uniqueRoute = [];
    route.forEach(point => {
      if (uniqueRoute.length === 0 || 
          calculateDistance(point, uniqueRoute[uniqueRoute.length - 1]) > 0.00001) {
        uniqueRoute.push(point);
      }
    });
    
    console.log('Final route has', uniqueRoute.length, 'unique points');
    return uniqueRoute;
  };

  // Find closest road point to a coordinate
  const findClosestRoadPoint = (coordinate, roadCoords) => {
    let closest = null;
    let minDistance = Infinity;
    
    roadCoords.forEach(coord => {
      const distance = calculateDistance(coordinate, coord);
      if (distance < minDistance) {
        minDistance = distance;
        closest = coord;
      }
    });
    
    return closest;
  };

  // DEPRECATED: Old findIntermediateRoadPoints function - replaced with improved version

  // Build graph from pre-structured GeoJSON (cemetery_full_graph.geojson)
  const buildGraphFromGeoJSON = (geoJsonData) => {
    const nodes = {};
    const edges = {};
    
    console.log('Building graph from pre-structured GeoJSON...');
    
    // Process nodes (type: "node")
    geoJsonData.features.forEach(feature => {
      if (feature.properties.type === 'node') {
        const nodeId = feature.properties.node_id;
        nodes[nodeId] = {
          id: nodeId,
          coordinates: feature.geometry.coordinates
        };
      }
    });
    
    // Process edges (type: "edge" or LineString with edge properties)
    geoJsonData.features.forEach(feature => {
      if (feature.properties.edge_id) {
        const roadId = feature.properties.road_id || '';
        
        // EXCLUDE only specific garden access roads from graph pathfinding
        // Be more specific to avoid excluding legitimate main roads
        if (roadId.includes('garden-access') || roadId.includes('chapel-access') ||
            roadId.includes('restroom-access') || roadId.includes('garden-connector')) {
          console.log('SKIPPING garden access road in graph:', roadId);
          return; // Skip this edge
        }
        
        const from = feature.properties.from;
        const to = feature.properties.to;
        const distance = feature.properties.length_m || calculateDistance(
          nodes[from]?.coordinates, 
          nodes[to]?.coordinates
        );
        
        if (nodes[from] && nodes[to]) {
          if (!edges[from]) edges[from] = [];
          if (!edges[to]) edges[to] = [];
          
          edges[from].push({ nodeId: to, distance });
          edges[to].push({ nodeId: from, distance });
        }
      }
    });
    
    // REMOVED: Manual n22 to n3 connection
    // This was potentially causing diagonal cuts through grave areas
    // Let's rely only on the actual road network connections from GeoJSON
    
    // REMOVED: Automatic nearby node connections
    // This was causing diagonal cuts through grave areas by connecting nodes
    // that should only be connected via the actual road network
    // The graph should only use the connections defined in the GeoJSON data
    
    // REMOVED: Virtual node approach was creating diagonal cuts
    // Instead, we'll use the existing road network and only cut through at the very end
    
    // Check graph connectivity
    const connectivityInfo = checkGraphConnectivity(nodes, edges);
    console.log('Graph construction complete:', {
      nodes: Object.keys(nodes).length,
      edges: Object.keys(edges).length,
      connectivity: connectivityInfo
    });
    
    return { nodes, edges };
  };

  // Build graph from optimized data structure
  const buildGraphFromOptimizedData = (graphData) => {
    const nodes = {};
    const edges = {};
    
    console.log('Building graph from optimized data structure...');
    
    // Process nodes
    graphData.nodes.forEach(node => {
      nodes[node.id] = {
        id: node.id,
        coordinates: node.coordinates,
        type: node.type,
        description: node.description
      };
    });
    
    // Process edges using adjacency list - pure distance only
    Object.entries(graphData.adjacency_list).forEach(([nodeId, connections]) => {
      edges[nodeId] = connections.map(conn => ({
        nodeId: conn.node,
        distance: conn.weight * 1000 // Convert to meters
      }));
    });
    
    console.log('Optimized graph construction complete:', {
      nodes: Object.keys(nodes).length,
      edges: Object.keys(edges).length,
      connectivity: graphData.pathfinding_metadata.connectivity_analysis
    });
    
    return { nodes, edges };
  };

  // Find nearest road point using optimized graph with perpendicular calculation
  const findNearestRoadPointOptimized = (coordinates, graphData) => {
    let closestPoint = null;
    let minDistance = Infinity;
    let closestRoadName = '';
    
    // First, check all nodes in the optimized graph
    graphData.nodes.forEach(node => {
      const distance = calculateDistance(coordinates, node.coordinates);
      
      if (distance < minDistance) {
        minDistance = distance;
        closestPoint = node.coordinates;
        closestRoadName = node.type || 'road_node';
      }
    });
    
    // Then, check perpendicular points on road segments
    graphData.edges.forEach(edge => {
      const sourceNode = graphData.nodes.find(n => n.id === edge.source);
      const targetNode = graphData.nodes.find(n => n.id === edge.target);
      
      if (sourceNode && targetNode) {
        // Calculate perpendicular point on this road segment
        const perpendicularPoint = findClosestPointOnLine(sourceNode.coordinates, targetNode.coordinates, coordinates);
        const distance = calculateDistance(coordinates, perpendicularPoint);
        
        if (distance < minDistance) {
          minDistance = distance;
          closestPoint = perpendicularPoint;
          closestRoadName = edge.name || edge.type || 'road_segment';
        }
      }
    });
    
    console.log(`Found nearest road point: ${closestRoadName} at distance ${minDistance.toFixed(1)}m`);
    
    return {
      point: closestPoint,
      distance: minDistance,
      roadName: closestRoadName
    };
  };


  // Check graph connectivity to ensure pathfinding will work
  const checkGraphConnectivity = (nodes, edges) => {
    const nodeIds = Object.keys(nodes);
    const connectedNodes = new Set();
    const disconnectedNodes = [];
    
    // Start from the first node and traverse the graph
    if (nodeIds.length > 0) {
      const queue = [nodeIds[0]];
      connectedNodes.add(nodeIds[0]);
      
      while (queue.length > 0) {
        const currentNode = queue.shift();
        const neighbors = edges[currentNode] || [];
        
        for (const neighbor of neighbors) {
          if (!connectedNodes.has(neighbor.nodeId)) {
            connectedNodes.add(neighbor.nodeId);
            queue.push(neighbor.nodeId);
          }
        }
      }
    }
    
    // Find disconnected nodes
    for (const nodeId of nodeIds) {
      if (!connectedNodes.has(nodeId)) {
        disconnectedNodes.push(nodeId);
      }
    }
    
    return {
      totalNodes: nodeIds.length,
      connectedNodes: connectedNodes.size,
      disconnectedNodes: disconnectedNodes.length,
      disconnectedNodeIds: disconnectedNodes,
      isFullyConnected: disconnectedNodes.length === 0
    };
  };

  // Find shortest path using graph structure
  const findGraphPath = (start, end, graph, endRoadPoint = null, actualEnd = null) => {
    const { nodes, edges } = graph;
    
    console.log('=== GRAPH PATHFINDING DEBUG ===');
    console.log('Graph stats:', {
      totalNodes: Object.keys(nodes).length,
      totalEdges: Object.values(edges).reduce((sum, edgeList) => sum + edgeList.length, 0) / 2
    });
    
    // Find nearest nodes to start and end points
    const startNode = findNearestGraphNode(start, nodes);
    const endNode = findNearestGraphNode(end, nodes);
    
    if (!startNode || !endNode) {
      console.warn('Could not find start or end nodes in graph');
      console.log('Start coordinate:', start);
      console.log('End coordinate:', end);
      console.log('Available nodes:', Object.keys(nodes).slice(0, 5));
      return [start, end];
    }
    
    console.log('Found start node:', startNode.id, 'at distance:', calculateDistance(start, startNode.coordinates));
    console.log('Found end node:', endNode.id, 'at distance:', calculateDistance(end, endNode.coordinates));
    console.log('Start node connections:', edges[startNode.id]?.length || 0);
    console.log('End node connections:', edges[endNode.id]?.length || 0);
    
    // IMPROVED COORDINATE-BASED PATHFINDING with advanced features
    console.log('=== COORDINATE-BASED PATHFINDING DEBUG ===');
    console.log('Start node:', startNode.id, 'at', startNode.coordinates);
    console.log('End node:', endNode.id, 'at', endNode.coordinates);
    
    // Target coordinates for comparison
    const targetLat = endNode.coordinates[1];
    const targetLng = endNode.coordinates[0];
    
    let path, pathNodes, current_node, total_distance, visited, loopCount, maxLoops;
    
    try {
      path = [startNode.coordinates];
      pathNodes = [startNode.id];
      current_node = startNode.id;
      total_distance = 0;
      visited = new Set([startNode.id]);
      
      loopCount = 0;
      maxLoops = 50;
      
      while (current_node !== endNode.id && loopCount < maxLoops) {
        loopCount++;
        const current_coord = nodes[current_node].coordinates;
        const currentLat = current_coord[1];
        const currentLng = current_coord[0];
        
        console.log(`Loop ${loopCount}: At ${current_node} (lng:${currentLng.toFixed(6)}, lat:${currentLat.toFixed(6)})`);
        console.log(`Target: (lng:${targetLng.toFixed(6)}, lat:${targetLat.toFixed(6)})`);
        
        // Special rule: n1 must always go to n2 first
        if (current_node === 'n1') {
          const n2_connection = edges[current_node]?.find(conn => conn.nodeId === 'n2');
          if (n2_connection) {
            console.log('ENFORCING RULE: n1 must always connect to n2 first');
            const next_node = 'n2';
            const next_coord = nodes[next_node].coordinates;
            const edge_distance = n2_connection.distance;
            
            path.push(next_coord);
            pathNodes.push(next_node);
            total_distance += edge_distance;
            current_node = next_node;
            visited.add(next_node);
            continue;
          }
        }
        
        // Special rule: n2 must always go to n58 first (intermediate intersection)
        if (current_node === 'n2') {
          const n58_connection = edges[current_node]?.find(conn => conn.nodeId === 'n58');
          if (n58_connection) {
            console.log('ENFORCING RULE: n2 must always connect to n58 first');
            const next_node = 'n58';
            const next_coord = nodes[next_node].coordinates;
            const edge_distance = n58_connection.distance;
            
            path.push(next_coord);
            pathNodes.push(next_node);
            total_distance += edge_distance;
            current_node = next_node;
            visited.add(next_node);
            continue;
          }
        }
        
        // COORDINATE-BASED DIRECTIONAL LOGIC (NOT DISTANCE-BASED)
        // Determine which direction we need to go based on coordinates
        const needGoNorth = targetLat > currentLat;  // Target is north of current position
        const needGoSouth = targetLat < currentLat;  // Target is south of current position  
        const needGoEast = targetLng > currentLng;   // Target is east of current position
        const needGoWest = targetLng < currentLng;   // Target is west of current position
        
        console.log('Direction needed:', {
          north: needGoNorth,
          south: needGoSouth,
          east: needGoEast,
          west: needGoWest,
          latDiff: (targetLat - currentLat).toFixed(6),
          lngDiff: (targetLng - currentLng).toFixed(6)
        });
        
        // Get all possible moves and score them based on coordinate direction
        const possible_moves = [];
        const all_connections = edges[current_node] || [];
        
        console.log(`Available connections from ${current_node}:`, all_connections.map(conn => conn.nodeId));
        
        for (const conn of all_connections) {
          const next_node = conn.nodeId;
          if (!visited.has(next_node)) {
            const next_coord = nodes[next_node].coordinates;
            const nextLat = next_coord[1];
            const nextLng = next_coord[0];
            const edge_distance = conn.distance;
            
            // Determine the direction this move will take us
            const moveGoesNorth = nextLat > currentLat;
            const moveGoesSouth = nextLat < currentLat;
            const moveGoesEast = nextLng > currentLng;
            const moveGoesWest = nextLng < currentLng;
            
            // COORDINATE-BASED SCORING (not distance-based)
            let coordinateScore = 0;
            
            // Primary scoring: Does this move go in the right direction?
            if (needGoNorth && moveGoesNorth) coordinateScore += 100;
            if (needGoSouth && moveGoesSouth) coordinateScore += 100;
            if (needGoEast && moveGoesEast) coordinateScore += 100;
            if (needGoWest && moveGoesWest) coordinateScore += 100;
            
            // Penalty for moving in wrong direction
            if (needGoNorth && moveGoesSouth) coordinateScore -= 200;
            if (needGoSouth && moveGoesNorth) coordinateScore -= 200;
            if (needGoEast && moveGoesWest) coordinateScore -= 200;
            if (needGoWest && moveGoesEast) coordinateScore -= 200;
            
            // Small bonus for getting closer to target coordinates
            const currentDistanceToTarget = Math.abs(targetLat - currentLat) + Math.abs(targetLng - currentLng);
            const nextDistanceToTarget = Math.abs(targetLat - nextLat) + Math.abs(targetLng - nextLng);
            const coordinateImprovement = currentDistanceToTarget - nextDistanceToTarget;
            coordinateScore += coordinateImprovement * 10; // Small bonus, not primary factor
            
        // ADVANCED FEATURE: Check if target is close to this road line (perpendicular point detection)
        // Use actualEnd (grave coordinates) instead of endNode.coordinates for perpendicular calculation
        const target_coords = actualEnd || endNode.coordinates;
        const perpendicular_point = findClosestPointOnLine(current_coord, next_coord, target_coords);
        const distance_to_perpendicular = calculateDistance(target_coords, perpendicular_point);
        const is_close_to_line = distance_to_perpendicular < 50; // Within 50m of the road line
        
        // Calculate perpendicular score (how close the target is to this road line)
        const perpendicular_score = is_close_to_line ? (100 - distance_to_perpendicular) : 0;
        
        // CHECK FOR PERPENDICULAR INTERRUPTION: Stop at perpendicular point if target is close to road line
        // But only if we're not in the very early stages of pathfinding (avoid stopping at first node)
        const distanceToTargetNode = calculateDistance(nodes[next_node].coordinates, endNode.coordinates);
        const isEarlyInPath = pathNodes.length < 2; // Only prevent stopping at first node, allow from 2nd node onwards
        const should_stop_at_perpendicular = is_close_to_line && distance_to_perpendicular < 30 && !isEarlyInPath; // Within 30m AND not at very start
            
            // IMPROVED: Prioritize perpendicular points over coordinate direction
            // If target is close to a road line, prioritize that over coordinate direction
            let combined_score;
            if (is_close_to_line && perpendicular_score > 50) {
              // Very high priority for perpendicular points - use perpendicular score as primary
              // Only add a tiny bonus for positive coordinate scores to break ties
              combined_score = perpendicular_score * 3 + (coordinateScore > 0 ? coordinateScore * 0.01 : 0);
            } else {
              // Normal scoring for non-perpendicular moves
              combined_score = perpendicular_score + coordinateScore;
            }
            
            possible_moves.push({
              node: next_node,
              coordinates: next_coord,
              edge_distance: edge_distance,
              coordinateScore: coordinateScore,
              moveDirection: {
                north: moveGoesNorth,
                south: moveGoesSouth,
                east: moveGoesEast,
                west: moveGoesWest
              },
              coordinateImprovement: coordinateImprovement,
              perpendicular_point: perpendicular_point,
              distance_to_perpendicular: distance_to_perpendicular,
              perpendicular_score: perpendicular_score,
              is_close_to_line: is_close_to_line,
              should_stop_at_perpendicular: should_stop_at_perpendicular,
              combined_score: combined_score
            });
          }
        }
      
        if (possible_moves.length === 0) {
          console.log('No more moves available!');
          break;
        }
        
        // ADVANCED FEATURE: Filter out moves that would create immediate loops (revisiting recent nodes)
        const recentNodes = pathNodes.slice(-3); // Last 3 nodes
        const filteredMoves = possible_moves.filter(move => !recentNodes.includes(move.node));
        
        if (filteredMoves.length === 0 && possible_moves.length > 0) {
          console.log('All moves would create immediate loops, using best available move');
          // Use the original possible_moves but log the loop
        } else {
          // Use filtered moves to avoid immediate loops
          possible_moves.length = 0;
          possible_moves.push(...filteredMoves);
        }
        
        // Sort by combined score (highest first)
        possible_moves.sort((a, b) => b.combined_score - a.combined_score);
        
        console.log('Possible moves (coordinate-based scoring):');
        for (const move of possible_moves) {
          const directionStr = [
            move.moveDirection.north ? 'NORTH' : '',
            move.moveDirection.south ? 'SOUTH' : '',
            move.moveDirection.east ? 'EAST' : '',
            move.moveDirection.west ? 'WEST' : ''
          ].filter(Boolean).join('+') || 'SAME';
          
          const perpendicular_info = move.is_close_to_line ? 
            `, PERPENDICULAR: ${move.distance_to_perpendicular.toFixed(1)}m from line (score: ${move.perpendicular_score.toFixed(1)})` : '';
          
          const interruption_info = move.should_stop_at_perpendicular ? 
            ` 🛑 WILL STOP AT PERPENDICULAR` : '';
          
          const scoring_method = move.is_close_to_line && move.perpendicular_score > 50 ? 
            ` [PERPENDICULAR-PRIORITIZED]` : ` [NORMAL-SCORING]`;
          
          console.log(`  ${move.node}: ${directionStr}, coordinate_score: ${move.coordinateScore}, improvement: ${move.coordinateImprovement.toFixed(3)}${perpendicular_info}${interruption_info} [COMBINED: ${move.combined_score.toFixed(1)}]${scoring_method}`);
        }
        
        // IMPROVED MOVE SELECTION: Better coordinate-based scoring
        let best_move = possible_moves[0];
        
        // Check if all moves have negative coordinate scores (all go in wrong direction)
        const allNegativeScores = possible_moves.every(move => move.coordinateScore < 0);
        
        if (allNegativeScores) {
          console.log('⚠️  All moves go in wrong direction! This suggests a graph connectivity issue.');
          console.log(`Available unvisited nodes from ${current_node}:`, all_connections.filter(conn => !visited.has(conn.nodeId)).map(conn => conn.nodeId));
          
          // Instead of distance-based fallback, let's improve the coordinate scoring
          // by giving partial credit for moves that go in the right direction on one axis
          console.log('🔧 Applying improved coordinate scoring...');
          
          for (const move of possible_moves) {
            // Give partial credit for moving in the right direction on at least one axis
            let improvedScore = move.coordinateScore;
            
            // If moving north when we need north, give partial credit even if going wrong on east/west
            if (needGoNorth && move.moveDirection.north && !move.moveDirection.south) {
              improvedScore += 50; // Partial credit for correct north movement
            }
            if (needGoSouth && move.moveDirection.south && !move.moveDirection.north) {
              improvedScore += 50; // Partial credit for correct south movement
            }
            if (needGoEast && move.moveDirection.east && !move.moveDirection.west) {
              improvedScore += 50; // Partial credit for correct east movement
            }
            if (needGoWest && move.moveDirection.west && !move.moveDirection.east) {
              improvedScore += 50; // Partial credit for correct west movement
            }
            
            move.coordinateScore = improvedScore;
            move.combined_score = move.perpendicular_score + improvedScore;
          }
          
          // Re-sort with improved scores
          possible_moves.sort((a, b) => b.combined_score - a.combined_score);
          best_move = possible_moves[0];
          
          console.log('Improved scoring applied. New best move:', best_move.node, 'with score:', best_move.coordinateScore);
        }
        
        const next_node = best_move.node;
        
        const bestDirectionStr = [
          best_move.moveDirection.north ? 'NORTH' : '',
          best_move.moveDirection.south ? 'SOUTH' : '',
          best_move.moveDirection.east ? 'EAST' : '',
          best_move.moveDirection.west ? 'WEST' : ''
        ].filter(Boolean).join('+') || 'SAME';
        
        if (best_move.is_close_to_line) {
          console.log(`→ Choosing ${next_node} (PERPENDICULAR PRIORITY: target is ${best_move.distance_to_perpendicular.toFixed(1)}m from this road line)`);
        } else {
          console.log(`→ Choosing ${next_node} (COORDINATE-BASED: ${bestDirectionStr}, score: ${best_move.coordinateScore})`);
        }
        
        // MODIFIED: Stop at perpendicular point when target is close to the current road segment
        // This ensures we stop at the perpendicular point on the road segment, not at the next node
        if (best_move.should_stop_at_perpendicular) {
          console.log(`🛑 PERPENDICULAR INTERRUPTION: Stopping at perpendicular point on road segment ${current_node} -> ${next_node}`);
          console.log(`   Perpendicular point: [${best_move.perpendicular_point[0]}, ${best_move.perpendicular_point[1]}]`);
          console.log(`   Distance to perpendicular: ${best_move.distance_to_perpendicular.toFixed(1)}m`);
          console.log(`   Target is ${best_move.distance_to_perpendicular.toFixed(1)}m from road line ${current_node} -> ${next_node}`);
          
          // Add the perpendicular point to the path instead of the next node
          path.push(best_move.perpendicular_point);
          pathNodes.push('PERPENDICULAR_POINT');
          
          // Calculate distance to perpendicular point instead of full edge distance
          const distanceToPerpendicular = calculateDistance(current_coord, best_move.perpendicular_point);
          total_distance += distanceToPerpendicular;
          
          console.log(`   Added perpendicular point to path, distance: ${distanceToPerpendicular.toFixed(1)}m`);
          break; // Exit the pathfinding loop - we've reached our stopping point
        }
      
        // Move to next node (only if not interrupted by perpendicular point)
        path.push(best_move.coordinates);
        pathNodes.push(next_node);
        total_distance += best_move.edge_distance;
        current_node = next_node;
        visited.add(next_node);
        
        // Safety check to prevent infinite loops
        if (pathNodes.length > 20) {
          console.log('Path too long, stopping!');
          break;
        }
      }
    
      // Check if we reached the target or hit safety limits
      if (current_node === endNode.id) {
        console.log('Successfully reached target node!');
      } else if (loopCount >= maxLoops) {
        console.log(`Hit maximum loop limit (${maxLoops}), stopping to prevent infinite loop`);
      } else {
        console.log('Did not reach target node, using partial path');
      }
    
      console.log('=== PATH RECONSTRUCTION DEBUG ===');
      console.log('Path nodes:', pathNodes);
      console.log('Path coordinates:', path);
      console.log(`Final path: ${pathNodes.join(' -> ')}`);
      console.log(`Total distance: ${total_distance.toFixed(1)}m`);
      console.log(`Path length: ${pathNodes.length} nodes`);
      
      // Add the actual start coordinates if different from road access point
      const distanceFromActualStart = calculateDistance(path[0], start);
      if (distanceFromActualStart > 1) {
        path.unshift(start);
        console.log(`Added initial segment from actual entrance location`);
      }
      
      // Check if perpendicular point was already added during pathfinding
      const hasPerpendicularPoint = pathNodes.includes('PERPENDICULAR_POINT');
      
      if (!hasPerpendicularPoint && endRoadPoint && endRoadPoint.point) {
        // Fallback: Add perpendicular point if it wasn't added during pathfinding
        const lastGraphNode = path[path.length - 1];
        const distanceToPerpendicular = calculateDistance(lastGraphNode, endRoadPoint.point);
        
        if (distanceToPerpendicular > 1) {
          path.push(endRoadPoint.point);
          console.log(`Added perpendicular point (fallback method)`);
        }
      } else if (hasPerpendicularPoint) {
        console.log(`Perpendicular point already added during pathfinding`);
      }
      
      // Finally, add the actual grave location
      if (actualEnd) {
        const lastPoint = path[path.length - 1];
        const distanceToGrave = calculateDistance(lastPoint, actualEnd);
        
        if (distanceToGrave > 1) {
          path.push(actualEnd);
          console.log(`Added final segment to grave location`);
        }
      }
      
      console.log('Graph path reconstructed with', path.length, 'points');
      return path;
      
    } catch (error) {
      console.error('Error in coordinate-based pathfinding:', error);
      
      // Fallback: Create a simple path
      const fallbackPath = [start];
      
      const startDistance = calculateDistance(start, startNode.coordinates);
      if (startDistance > 0.0001) {
        fallbackPath.push(startNode.coordinates);
      }
      
      const endDistance = calculateDistance(end, endNode.coordinates);
      if (endDistance > 0.0001) {
        fallbackPath.push(endNode.coordinates);
      }
      
      fallbackPath.push(end);
      
      console.log('Fallback path created with', fallbackPath.length, 'points');
      return fallbackPath;
    }
  };


  // Find perpendicular point on a line segment
  const findPerpendicularPointOnSegment = (point, lineStart, lineEnd) => {
    const [px, py] = point;
    const [x1, y1] = lineStart;
    const [x2, y2] = lineEnd;
    
    // Vector from lineStart to lineEnd
    const dx = x2 - x1;
    const dy = y2 - y1;
    
    // Vector from lineStart to point
    const px_dx = px - x1;
    const py_dy = py - y1;
    
    // Calculate t (parameter along the line)
    const lineLengthSquared = dx * dx + dy * dy;
    
    if (lineLengthSquared === 0) {
      // Line segment is actually a point
      return lineStart;
    }
    
    let t = (px_dx * dx + py_dy * dy) / lineLengthSquared;
    
    // Clamp t to [0, 1] to stay on the segment
    t = Math.max(0, Math.min(1, t));
    
    // Calculate the perpendicular point
    const perpendicularX = x1 + t * dx;
    const perpendicularY = y1 + t * dy;
    
    return [perpendicularX, perpendicularY];
  };

  // Find nearest node in graph (fallback for graph pathfinding)
  const findNearestGraphNode = (coordinates, nodes) => {
    let closest = null;
    let minDistance = Infinity;
    
    Object.values(nodes).forEach(node => {
      const distance = calculateDistance(coordinates, node.coordinates);
      if (distance < minDistance) {
        minDistance = distance;
        closest = node;
      }
    });
    
    return closest;
  };

  // Find the nearest point on the road network to a given coordinate
  const findNearestPointOnRoadNetwork = (coordinates, geoJsonData, nodes) => {
    let closestPoint = null;
    let minDistance = Infinity;
    let closestNode = null;
    
    // Check all main road edges (not garden access roads)
    geoJsonData.features.forEach(feature => {
      if (feature.properties.edge_id && feature.geometry.type === 'LineString') {
        const roadId = feature.properties.road_id || '';
        
        // Only check main roads (exclude garden access roads)
        if (!(roadId.includes('garden') || roadId.includes('access') || 
              roadId.includes('connector') || roadId.includes('chapel-access') ||
              roadId.includes('restroom-access'))) {
          
          const roadCoords = feature.geometry.coordinates;
          
          // Check each segment of the road
          for (let i = 0; i < roadCoords.length - 1; i++) {
            const segmentStart = roadCoords[i];
            const segmentEnd = roadCoords[i + 1];
            
            // Find perpendicular point on this segment
            const perpendicularPoint = findPerpendicularPointOnSegment(
              coordinates, segmentStart, segmentEnd
            );
            
            const distance = calculateDistance(coordinates, perpendicularPoint);
            
            if (distance < minDistance) {
              minDistance = distance;
              closestPoint = perpendicularPoint;
              
              // Find the nearest graph node to this point
              closestNode = findNearestGraphNode(perpendicularPoint, nodes);
            }
          }
        }
      }
    });
    
    console.log('Found nearest point on road network:', {
      point: closestPoint,
      distance: minDistance.toFixed(1),
      nearestNode: closestNode?.id
    });
    
    return closestNode || findNearestGraphNode(coordinates, nodes);
  };

  // Build a proper road network from GeoJSON
  const buildProperRoadNetwork = (geoJsonData) => {
    const nodes = {}; // coordinate -> node
    const edges = {}; // nodeId -> connected nodes
    const roadSegments = []; // all road segments
    
    // console.log('Building proper road network from GeoJSON...');
    // console.log('Total features in GeoJSON:', geoJsonData.features.length);
    
    // Process all road features
    let roadFeatureCount = 0;
    geoJsonData.features.forEach((feature, featureIndex) => {
      if (feature.properties.type === 'main' || feature.properties.type === 'pathway') {
        roadFeatureCount++;
        // console.log(`Processing road feature ${roadFeatureCount}:`, feature.properties.name, feature.properties.type);
        if (feature.geometry.coordinates && feature.geometry.coordinates.length > 0) {
          const coords = feature.geometry.coordinates;
          const roadName = feature.properties.name || `Road-${featureIndex}`;
          
          // Add all coordinates as nodes
          coords.forEach((coord, coordIndex) => {
            const nodeId = `${coord[0].toFixed(6)},${coord[1].toFixed(6)}`;
            
            if (!nodes[nodeId]) {
              nodes[nodeId] = {
                id: nodeId,
                coordinates: coord,
                roads: [],
                connections: []
              };
            }
            
            const node = nodes[nodeId];
            if (!node.roads.includes(roadName)) {
              node.roads.push(roadName);
            }
            
            // Connect to previous node in same road
            if (coordIndex > 0) {
              const prevCoord = coords[coordIndex - 1];
              const prevNodeId = `${prevCoord[0].toFixed(6)},${prevCoord[1].toFixed(6)}`;
              
              if (!edges[nodeId]) edges[nodeId] = [];
              if (!edges[prevNodeId]) edges[prevNodeId] = [];
              
              const distance = calculateDistance(coord, prevCoord);
              edges[nodeId].push({ nodeId: prevNodeId, distance, roadName });
              edges[prevNodeId].push({ nodeId, distance, roadName });
            }
          });
          
          // Store road segment
          roadSegments.push({
            name: roadName,
            type: feature.properties.type,
            coordinates: coords
          });
        }
      }
    });
    
    // Connect nearby nodes (intersections)
    const nodeArray = Object.values(nodes);
    for (let i = 0; i < nodeArray.length; i++) {
      for (let j = i + 1; j < nodeArray.length; j++) {
        const node1 = nodeArray[i];
        const node2 = nodeArray[j];
        const distance = calculateDistance(node1.coordinates, node2.coordinates);
        
        // Connect nodes that are very close (intersections) - increased threshold for better connectivity
        if (distance < 0.0002) { // ~20 meters for better road connectivity
          if (!edges[node1.id]) edges[node1.id] = [];
          if (!edges[node2.id]) edges[node2.id] = [];
          
          edges[node1.id].push({ nodeId: node2.id, distance, roadName: 'intersection' });
          edges[node2.id].push({ nodeId: node1.id, distance, roadName: 'intersection' });
        }
      }
    }
    
    // console.log('=== ROAD NETWORK BUILD DEBUG ===');
    // console.log('Road network built:', {
    //   nodes: Object.keys(nodes).length,
    //   edges: Object.values(edges).reduce((sum, edgeList) => sum + edgeList.length, 0) / 2,
    //   roadSegments: roadSegments.length
    // });
    
    return { nodes, edges, roadSegments };
  };

  // Generate turn-by-turn instructions from route
  const generateTurnByTurnInstructions = (route, roadNetwork) => {
    if (!route || route.length < 2) {
      return [{
        type: 'start',
        instruction: 'Start at entrance',
        distance: 0,
        icon: '📍'
      }, {
        type: 'arrive',
        instruction: 'Arrive at destination',
        distance: 0,
        icon: '🎯'
      }];
    }

    const instructions = [];
    instructions.push({
      type: 'start',
      instruction: 'Start at entrance',
      distance: 0,
      icon: '📍'
    });

    let currentRoad = null;
    let totalDistance = 0;
    const roadSegments = [];
    let segmentDistance = 0;
    const maxSegmentDistance = 50; // Create new segments every 50 meters

    // First pass: collect all road segments
    for (let i = 1; i < route.length; i++) {
      const prevPoint = route[i - 1];
      const currentPoint = route[i];
      const distance = calculateDistance(prevPoint, currentPoint);
      totalDistance += distance;
      segmentDistance += distance;

      // Find which road this segment belongs to
      const roadName = findRoadNameForSegment(prevPoint, currentPoint, roadNetwork, i, route.length);
      
      // Create new segment if road changes OR if we've traveled far enough on same road
      if (roadName && (roadName !== currentRoad || segmentDistance > maxSegmentDistance)) {
        if (currentRoad) {
          roadSegments.push({
            name: currentRoad,
            distance: segmentDistance
          });
        }
        currentRoad = roadName;
        segmentDistance = 0; // Reset segment distance
        console.log('Found road segment:', roadName, 'at total distance:', totalDistance);
      }
    }

    // Add the last road segment
    if (currentRoad) {
      roadSegments.push({
        name: currentRoad,
        distance: segmentDistance
      });
    }

    // If we still only have one road segment, create multiple based on route analysis
    if (roadSegments.length <= 1 && route.length > 2) {
      console.log('Creating multiple road segments from route analysis...');
      roadSegments.length = 0; // Clear existing segments
      
      // Analyze route to create multiple segments
      const routeLength = route.length;
      const segmentSize = Math.max(2, Math.floor(routeLength / 3)); // Create 2-3 segments
      
      for (let i = 0; i < segmentSize; i++) {
        const startIdx = Math.floor((i * routeLength) / segmentSize);
        const endIdx = Math.floor(((i + 1) * routeLength) / segmentSize);
        
        if (startIdx < endIdx && endIdx < routeLength) {
          const startPoint = route[startIdx];
          const endPoint = route[endIdx];
          const roadName = findRoadNameForSegment(startPoint, endPoint, roadNetwork, i, segmentSize);
          const distance = calculateDistance(startPoint, endPoint);
          
          roadSegments.push({
            name: roadName,
            distance: distance
          });
        }
      }
    }

    // Generate instructions for each road segment
    console.log('Generated road segments:', roadSegments);
    roadSegments.forEach((segment, index) => {
      let enhancedInstruction = segment.name;
      
      // Enhanced road descriptions based on GeoJSON data
      if (segment.name.includes('Main Entrance Road')) {
        enhancedInstruction = 'Continue on Main Entrance Road';
      } else if (segment.name.includes('Central Loop')) {
        enhancedInstruction = 'Take Central Loop Road';
      } else if (segment.name.includes('Northern')) {
        enhancedInstruction = 'Follow Northern Cemetery Road';
      } else if (segment.name.includes('Eastern')) {
        enhancedInstruction = 'Continue on Eastern Cemetery Road';
      } else if (segment.name.includes('Garden A')) {
        enhancedInstruction = 'Follow Western Garden Access';
      } else if (segment.name.includes('Garden B')) {
        enhancedInstruction = 'Follow Garden B Access Path';
      } else if (segment.name.includes('Garden C')) {
        enhancedInstruction = 'Follow Garden C Main Path';
      } else if (segment.name.includes('Garden')) {
        enhancedInstruction = `Follow ${segment.name}`;
      } else {
        enhancedInstruction = `Continue on ${segment.name}`;
      }
      
      instructions.push({
        type: 'turn',
        instruction: enhancedInstruction,
        distance: segment.distance,
        icon: '➡️'
      });
    });

    // Add arrival instruction
    instructions.push({
      type: 'arrive',
      instruction: 'Arrive at destination',
      distance: 0,
      icon: '🎯'
    });

    return instructions;
  };

  // Find road name for a segment between two points
  const findRoadNameForSegment = (point1, point2, roadNetwork, routeIndex = 0, totalRouteLength = 1) => {
    if (!roadNetwork || !roadNetwork.edges) {
      return getRoadNameByPosition(routeIndex, totalRouteLength);
    }
    
    // Find the edge that connects these two points
    const nodeId1 = `${point1[0].toFixed(6)},${point1[1].toFixed(6)}`;
    const nodeId2 = `${point2[0].toFixed(6)},${point2[1].toFixed(6)}`;
    
    // Try exact match first
    if (roadNetwork.edges[nodeId1]) {
      const edges = roadNetwork.edges[nodeId1];
      for (const edge of edges) {
        if (edge.nodeId === nodeId2) {
          return edge.roadName || getRoadNameByPosition(routeIndex, totalRouteLength);
        }
      }
    }
    
    // Try reverse direction
    if (roadNetwork.edges[nodeId2]) {
      const edges = roadNetwork.edges[nodeId2];
      for (const edge of edges) {
        if (edge.nodeId === nodeId1) {
          return edge.roadName || getRoadNameByPosition(routeIndex, totalRouteLength);
        }
      }
    }
    
    // If no exact match, find the nearest node and get its road
    let nearestNode = null;
    let minDistance = Infinity;
    
    Object.values(roadNetwork.nodes || {}).forEach(node => {
      const dist1 = calculateDistance(point1, node.coordinates);
      const dist2 = calculateDistance(point2, node.coordinates);
      const totalDist = dist1 + dist2;
      
      if (totalDist < minDistance) {
        minDistance = totalDist;
        nearestNode = node;
      }
    });
    
    if (nearestNode && nearestNode.roads && nearestNode.roads.length > 0) {
      return nearestNode.roads[0]; // Return the first road name
    }
    
    return getRoadNameByPosition(routeIndex, totalRouteLength);
  };

  // Get road name based on position in route
  const getRoadNameByPosition = (index, totalLength) => {
    const roads = [
      'Main Entrance Road',
      'Central Loop Road', 
      'Northern Cemetery Road',
      'Eastern Cemetery Road',
      'Western Garden Access',
      'Garden B Access Path',
      'Garden C Main Path'
    ];
    
    // Use position to select appropriate road
    const roadIndex = Math.min(index, roads.length - 1);
    return roads[roadIndex];
  };

  // Find road path using A* algorithm
  const findRoadPath = (start, end, roadNetwork) => {
    const { nodes, edges } = roadNetwork;
    
    console.log('=== PATHFINDING DEBUG ===');
    console.log('Road network stats:', {
      totalNodes: Object.keys(nodes).length,
      totalEdges: Object.values(edges).reduce((sum, edgeList) => sum + edgeList.length, 0) / 2
    });
    
    // Find nearest nodes to start and end points
    const startNode = findNearestNode(start, nodes);
    const endNode = findNearestNode(end, nodes);
    
    if (!startNode || !endNode) {
      console.warn('Could not find start or end nodes');
      console.log('Start coordinate:', start);
      console.log('End coordinate:', end);
      console.log('Available nodes:', Array.from(nodes.keys()).slice(0, 5));
      return [start, end];
    }
    
    console.log('Found start node:', startNode.id, 'at distance:', calculateDistance(start, startNode.coordinates));
    console.log('Found end node:', endNode.id, 'at distance:', calculateDistance(end, endNode.coordinates));
    console.log('Start node connections:', edges[startNode.id]?.length || 0);
    console.log('End node connections:', edges[endNode.id]?.length || 0);
    
    // A* algorithm
    const openSet = [startNode.id];
    const cameFrom = {};
    const gScore = {};
    const fScore = {};
    
    // Initialize scores
    Object.values(nodes).forEach((node) => {
      gScore[node.id] = Infinity;
      fScore[node.id] = Infinity;
    });
    
    gScore[startNode.id] = 0;
    fScore[startNode.id] = calculateDistance(startNode.coordinates, endNode.coordinates);
    
    while (openSet.length > 0) {
      // Find node with lowest fScore
      let current = null;
      let lowestF = Infinity;
      
      for (const nodeId of openSet) {
        if (fScore[nodeId] < lowestF) {
          lowestF = fScore[nodeId];
          current = nodeId;
        }
      }
      
      if (current === endNode.id) {
        // Reconstruct path
        const path = [];
        let nodeId = endNode.id;
        
        while (nodeId) {
          path.unshift(nodes[nodeId].coordinates);
          nodeId = cameFrom[nodeId];
        }
        
        console.log('Path reconstructed with', path.length, 'points');
        return path;
      }
      
      const currentIndex = openSet.indexOf(current);
      if (currentIndex > -1) {
        openSet.splice(currentIndex, 1);
      }
      
      // Check neighbors
      const neighbors = edges[current] || [];
      for (const neighbor of neighbors) {
        const tentativeG = gScore[current] + neighbor.distance;
        
        if (tentativeG < gScore[neighbor.nodeId]) {
          cameFrom[neighbor.nodeId] = current;
          gScore[neighbor.nodeId] = tentativeG;
          fScore[neighbor.nodeId] = tentativeG + calculateDistance(nodes[neighbor.nodeId].coordinates, endNode.coordinates);
          
          if (!openSet.includes(neighbor.nodeId)) {
            openSet.push(neighbor.nodeId);
          }
        }
      }
    }
    
    console.warn('No path found with A* algorithm, trying simplified pathfinding...');
    
    // Fallback: Try to find a path by following road segments
    const fallbackPath = findSimplifiedPath(start, end, roadNetwork);
    if (fallbackPath.length > 2) {
      console.log('Found fallback path with', fallbackPath.length, 'points');
      return fallbackPath;
    }
    
    console.warn('All pathfinding methods failed, returning direct route');
    return [start, end];
  };

  // Simplified pathfinding fallback
  const findSimplifiedPath = (start, end, roadNetwork) => {
    const { nodes, roadSegments } = roadNetwork;
    
    console.log('Trying simplified pathfinding...');
    
    // Find the road segment closest to start
    let closestStartSegment = null;
    let minStartDistance = Infinity;
    
    roadSegments.forEach((segment, index) => {
      segment.coordinates.forEach(coord => {
        const distance = calculateDistance(start, coord);
        if (distance < minStartDistance) {
          minStartDistance = distance;
          closestStartSegment = { segment, coord, index };
        }
      });
    });
    
    // Find the road segment closest to end
    let closestEndSegment = null;
    let minEndDistance = Infinity;
    
    roadSegments.forEach((segment, index) => {
      segment.coordinates.forEach(coord => {
        const distance = calculateDistance(end, coord);
        if (distance < minEndDistance) {
          minEndDistance = distance;
          closestEndSegment = { segment, coord, index };
        }
      });
    });
    
    if (!closestStartSegment || !closestEndSegment) {
      console.warn('Could not find road segments near start/end points');
      return [start, end];
    }
    
    console.log('Found road segments:', {
      startSegment: closestStartSegment.segment.name,
      endSegment: closestEndSegment.segment.name
    });
    
    // Build a more detailed path following road segments
    const path = [start];
    
    // Add the closest road point to start (only if it's different from start)
    if (minStartDistance > 0.0001) {
      path.push(closestStartSegment.coord);
    }
    
    // If different segments, add intermediate points along the road network
    if (closestStartSegment.index !== closestEndSegment.index) {
      // Find intermediate road points between start and end segments
      const intermediatePoints = findIntermediateRoadPoints(
        closestStartSegment.coord, 
        closestEndSegment.coord, 
        roadSegments
      );
      path.push(...intermediatePoints);
      
      // Add the closest road point to end (only if it's different from end)
      if (minEndDistance > 0.0001) {
        path.push(closestEndSegment.coord);
      }
    }
    
    // Add the end point
    path.push(end);
    
    console.log('Simplified path built with', path.length, 'points');
    console.log('Simplified path coordinates:', path);
    console.log('Simplified path distances:', path.map((point, i) => 
      i > 0 ? calculateDistance(path[i-1], point) : 0
    ));
    return path;
  };

  // Find intermediate road points between two coordinates
  const findIntermediateRoadPoints = (startCoord, endCoord, roadSegments) => {
    const intermediatePoints = [];
    
    // Find road segments that are between start and end
    roadSegments.forEach(segment => {
      if (segment.coordinates && segment.coordinates.length > 0) {
        // Check if this road segment is roughly between start and end
        const segmentMidpoint = segment.coordinates[Math.floor(segment.coordinates.length / 2)];
        const startToMid = calculateDistance(startCoord, segmentMidpoint);
        const midToEnd = calculateDistance(segmentMidpoint, endCoord);
        const startToEnd = calculateDistance(startCoord, endCoord);
        
        // If the segment is roughly on the path (not too far off the direct line)
        if (startToMid + midToEnd < startToEnd * 1.8) {
          // Add key points from this road segment
          const step = Math.max(1, Math.floor(segment.coordinates.length / 4));
          for (let i = step; i < segment.coordinates.length - step; i += step) {
            const coord = segment.coordinates[i];
            const distFromStart = calculateDistance(startCoord, coord);
            const distFromEnd = calculateDistance(coord, endCoord);
            
            // Only add if it's not too close to start/end and is meaningful
            if (distFromStart > 5 && distFromEnd > 5) {
              intermediatePoints.push(coord);
            }
          }
        }
      }
    });
    
    // Sort intermediate points by distance from start
    intermediatePoints.sort((a, b) => {
      const distA = calculateDistance(startCoord, a);
      const distB = calculateDistance(startCoord, b);
      return distA - distB;
    });
    
    // Limit to reasonable number of intermediate points
    return intermediatePoints.slice(0, 8);
  };

  // Find nearest node to a coordinate
  const findNearestNode = (coordinate, nodes) => {
    let nearest = null;
    let minDistance = Infinity;
    
    Object.values(nodes).forEach((node) => {
      const distance = calculateDistance(coordinate, node.coordinates);
      if (distance < minDistance) {
        minDistance = distance;
        nearest = node;
      }
    });
    
    return nearest;
  };

  // Find waypoint-based path (more reliable approach)
  const findWaypointPath = (start, end, roadNetwork) => {
    const { nodes, roadSegments } = roadNetwork;
    
    // Find key waypoints along the route
    const waypoints = [];
    
    // Add start point
    waypoints.push(start);
    
    // Find intermediate waypoints based on road segments
    roadSegments.forEach(segment => {
      if (segment.coordinates && segment.coordinates.length > 0) {
        // Add key points from each road segment
        const midPoint = segment.coordinates[Math.floor(segment.coordinates.length / 2)];
        waypoints.push(midPoint);
      }
    });
    
    // Add end point
    waypoints.push(end);
    
    // Connect waypoints through road network
    const path = [];
    for (let i = 0; i < waypoints.length - 1; i++) {
      const segmentStart = waypoints[i];
      const segmentEnd = waypoints[i + 1];
      
      // Find nearest nodes
      const startNode = findNearestNode(segmentStart, nodes);
      const endNode = findNearestNode(segmentEnd, nodes);
      
      if (startNode && endNode) {
        // Add start node
        path.push(startNode.coordinates);
        
        // Add end node
        path.push(endNode.coordinates);
      }
    }
    
    // Remove duplicates and return
    const uniquePath = [];
    path.forEach(point => {
      if (uniquePath.length === 0 || 
          calculateDistance(point, uniquePath[uniquePath.length - 1]) > 0.00001) {
        uniquePath.push(point);
      }
    });
    
    return uniquePath;
  };

  // Get directions from Mapbox Directions API
  const getMapboxDirections = async (start, end, accessToken) => {
    try {
      const startCoords = `${start[0]},${start[1]}`;
      const endCoords = `${end[0]},${end[1]}`;
      
      const url = `https://api.mapbox.com/directions/v5/mapbox/walking/${startCoords};${endCoords}?` +
        `access_token=${accessToken}&` +
        `geometries=geojson&` +
        `steps=true&` +
        `overview=full`;
      
      const response = await fetch(url);
      const data = await response.json();
      
      if (data.routes && data.routes.length > 0) {
        return data.routes[0];
      }
      
      return null;
    } catch (error) {
      console.error('Mapbox Directions API error:', error);
      return null;
    }
  };

  // Extract road segments from GeoJSON (simplified approach like Leaflet)
  const extractRoadSegments = (geoJsonData) => {
    const roads = [];
    
    // Process all road features from your GeoJSON
    geoJsonData.features.forEach((feature, featureIndex) => {
      if (feature.properties.type === 'main' || feature.properties.type === 'pathway') {
        if (feature.geometry.coordinates && feature.geometry.coordinates.length > 0) {
          const coords = feature.geometry.coordinates;
          
          // Create road segments (same as Leaflet approach)
          for (let i = 0; i < coords.length - 1; i++) {
            roads.push({
              id: `${featureIndex}-${i}`,
              name: feature.properties.name || `Road-${featureIndex}`,
              type: feature.properties.type,
              start: coords[i],
              end: coords[i + 1],
              coordinates: [coords[i], coords[i + 1]]
            });
          }
        }
      }
    });
    
    console.log('Extracted road segments:', roads.length);
    console.log('Sample road segments:', roads.slice(0, 3));
    return roads;
  };

  // Find nearest road point (simplified approach like Leaflet)
  const findNearestRoadPointSimple = (point, roads) => {
    let nearestPoint = null;
    let minDistance = Infinity;
    let roadIndex = null;

    roads.forEach((road, index) => {
      const pointOnRoad = findClosestPointOnLine(road.start, road.end, point);
      const distance = calculateDistance(point, pointOnRoad);

      if (distance < minDistance) {
        minDistance = distance;
        nearestPoint = pointOnRoad;
        roadIndex = index;
      }
    });

    console.log('Found nearest road point:', { point: nearestPoint, roadIndex, distance: minDistance });
    return { point: nearestPoint, roadIndex, distance: minDistance };
  };

  // Find closest point on line segment (same as Leaflet)
  const findClosestPointOnLine = (lineStart, lineEnd, targetPoint) => {
    const lineVector = [
      lineEnd[0] - lineStart[0],
      lineEnd[1] - lineStart[1]
    ];
    const pointVector = [
      targetPoint[0] - lineStart[0],
      targetPoint[1] - lineStart[1]
    ];

    const lengthSquared = lineVector[0] * lineVector[0] + lineVector[1] * lineVector[1];
    const t = Math.max(0, Math.min(1, (
      pointVector[0] * lineVector[0] + pointVector[1] * lineVector[1]
    ) / lengthSquared));

    return [
      lineStart[0] + t * lineVector[0],
      lineStart[1] + t * lineVector[1]
    ];
  };

  // Check if two road segments are connected (same as Leaflet)
  const areRoadsConnectedSimple = (road1, road2) => {
    const start1 = road1.start;
    const end1 = road1.end;
    const start2 = road2.start;
    const end2 = road2.end;

    // Check if any endpoints are close to the other road segment
    const point1OnRoad2 = findClosestPointOnLine(start2, end2, start1);
    const point2OnRoad1 = findClosestPointOnLine(start1, end1, start2);
    const point3OnRoad2 = findClosestPointOnLine(start2, end2, end1);
    const point4OnRoad1 = findClosestPointOnLine(start1, end1, end2);

    // Calculate distances between endpoints and their closest points on the other road
    const distance1 = calculateDistance(start1, point1OnRoad2);
    const distance2 = calculateDistance(start2, point2OnRoad1);
    const distance3 = calculateDistance(end1, point3OnRoad2);
    const distance4 = calculateDistance(end2, point4OnRoad1);

    // Use a threshold for connectivity (same as Leaflet)
    const threshold = 0.0001; // ~10 meters
    return Math.min(distance1, distance2, distance3, distance4) < threshold;
  };

  // Find path through roads using simple algorithm (same as Leaflet)
  const findPathThroughRoadsSimple = (startPoint, endPoint, roads) => {
    let path = [];
    let currentRoadIndex = startPoint.roadIndex;
    let targetRoadIndex = endPoint.roadIndex;

    // If we're on the same road, we're done
    if (currentRoadIndex === targetRoadIndex) {
      return [];
    }

    // Find connected roads that lead to the target (same as Leaflet)
    let visited = new Set([currentRoadIndex]);
    let queue = [{
      roadIndex: currentRoadIndex,
      path: []
    }];

    while (queue.length > 0) {
      const { roadIndex, path: currentPath } = queue.shift();

      // Check all roads for connections
      roads.forEach((_, index) => {
        if (visited.has(index)) return;

        if (areRoadsConnectedSimple(roads[roadIndex], roads[index])) {
          const newPath = [...currentPath];
          
          // Find the connection point between the roads
          const point1OnRoad2 = findClosestPointOnLine(roads[index].start, roads[index].end, roads[roadIndex].start);
          const point2OnRoad1 = findClosestPointOnLine(roads[roadIndex].start, roads[roadIndex].end, roads[index].start);
          
          // Use the point that's closest to both roads
          const distance1 = calculateDistance(roads[roadIndex].start, point1OnRoad2);
          const distance2 = calculateDistance(roads[index].start, point2OnRoad1);
          
          const connectionPoint = distance1 < distance2 ? point1OnRoad2 : point2OnRoad1;
          newPath.push(connectionPoint);

          if (index === targetRoadIndex) {
            // We found a path to the target road
            path = newPath;
            return;
          }

          queue.push({
            roadIndex: index,
            path: newPath
          });
          visited.add(index);
        }
      });
    }

    console.log('Found path through roads:', path.length, 'points');
    return path;
  };

  // Build road network from GeoJSON
  const buildRoadNetwork = (geoJsonData) => {
    const nodes = new Map();
    const edges = new Map();
    
    // Process all road features
    geoJsonData.features.forEach((feature, featureIndex) => {
      if (feature.properties.type === 'main' || feature.properties.type === 'pathway') {
        if (feature.geometry.coordinates) {
          const coords = feature.geometry.coordinates;
          
          coords.forEach((coord, coordIndex) => {
            const nodeId = `${featureIndex}-${coordIndex}`;
            
            // Add node
            nodes.set(nodeId, {
              id: nodeId,
              coordinates: coord,
              roadName: feature.properties.name || `Road-${featureIndex}`
            });
            
            // Connect to next point on same road
            if (coordIndex < coords.length - 1) {
              const nextNodeId = `${featureIndex}-${coordIndex + 1}`;
              const distance = calculateDistance(coord, coords[coordIndex + 1]);
              
              if (!edges.has(nodeId)) edges.set(nodeId, new Map());
              if (!edges.has(nextNodeId)) edges.set(nextNodeId, new Map());
              
              edges.get(nodeId).set(nextNodeId, distance);
              edges.get(nextNodeId).set(nodeId, distance);
            }
          });
        }
      }
    });
    
    // Connect nearby nodes (intersections)
    const nodeArray = Array.from(nodes.values());
    for (let i = 0; i < nodeArray.length; i++) {
      for (let j = i + 1; j < nodeArray.length; j++) {
        const node1 = nodeArray[i];
        const node2 = nodeArray[j];
        const distance = calculateDistance(node1.coordinates, node2.coordinates);
        
        if (distance < 20) { // 20 meter threshold for intersections
          if (!edges.has(node1.id)) edges.set(node1.id, new Map());
          if (!edges.has(node2.id)) edges.set(node2.id, new Map());
          
          edges.get(node1.id).set(node2.id, distance);
          edges.get(node2.id).set(node1.id, distance);
        }
      }
    }
    
    return { nodes, edges };
  };


  // Find shortest path using Dijkstra's algorithm
  const findShortestPath = (startNode, endNode, roadNetwork) => {
    if (!startNode || !endNode) return [];
    
    const distances = new Map();
    const previous = new Map();
    const unvisited = new Set();
    
    // Initialize distances
    roadNetwork.nodes.forEach((node) => {
      distances.set(node.id, node.id === startNode.id ? 0 : Infinity);
      unvisited.add(node.id);
    });
    
    while (unvisited.size > 0) {
      // Find unvisited node with smallest distance
      let currentNode = null;
      let minDistance = Infinity;
      
      unvisited.forEach(nodeId => {
        const distance = distances.get(nodeId);
        if (distance < minDistance) {
          minDistance = distance;
          currentNode = nodeId;
        }
      });
      
      if (currentNode === null || currentNode === endNode.id) {
        break;
      }
      
      unvisited.delete(currentNode);
      
      // Check neighbors
      const neighbors = roadNetwork.edges.get(currentNode);
      if (neighbors) {
        neighbors.forEach((edgeDistance, neighborId) => {
          if (unvisited.has(neighborId)) {
            const newDistance = distances.get(currentNode) + edgeDistance;
            if (newDistance < distances.get(neighborId)) {
              distances.set(neighborId, newDistance);
              previous.set(neighborId, currentNode);
            }
          }
        });
      }
    }
    
    // Reconstruct path
    const path = [];
    let currentNode = endNode.id;
    
    while (currentNode !== undefined) {
      const node = roadNetwork.nodes.get(currentNode);
      if (node) {
        path.unshift(node.coordinates);
      }
      currentNode = previous.get(currentNode);
    }
    
    return path;
  };

  // Build a simple road graph from GeoJSON data
  const buildSimpleRoadGraph = (geoJsonData) => {
    const graph = {
      nodes: new Map(),
      edges: new Map()
    };
    
    // Get all road features
    const roadFeatures = geoJsonData.features.filter(feature => 
      feature.properties.type === 'main' || feature.properties.type === 'pathway'
    );
    
    console.log('Building road graph from', roadFeatures.length, 'road features');
    
    // Create nodes for each road coordinate
    roadFeatures.forEach((feature, featureIndex) => {
      if (feature.geometry.coordinates) {
        const coords = feature.geometry.coordinates;
        
        coords.forEach((coord, coordIndex) => {
          const nodeId = `${featureIndex}-${coordIndex}`;
          
          graph.nodes.set(nodeId, {
            id: nodeId,
            coordinates: coord,
            roadName: feature.properties.name || `Road-${featureIndex}`,
            roadType: feature.properties.type
          });
          
          // Connect to next point on same road
          if (coordIndex < coords.length - 1) {
            const nextNodeId = `${featureIndex}-${coordIndex + 1}`;
            const distance = calculateDistance(coord, coords[coordIndex + 1]);
            
            if (!graph.edges.has(nodeId)) {
              graph.edges.set(nodeId, new Map());
            }
            if (!graph.edges.has(nextNodeId)) {
              graph.edges.set(nextNodeId, new Map());
            }
            
            graph.edges.get(nodeId).set(nextNodeId, distance);
            graph.edges.get(nextNodeId).set(nodeId, distance);
          }
        });
      }
    });
    
    // Connect nearby nodes from different roads (intersections)
    const nodeArray = Array.from(graph.nodes.values());
    for (let i = 0; i < nodeArray.length; i++) {
      for (let j = i + 1; j < nodeArray.length; j++) {
        const node1 = nodeArray[i];
        const node2 = nodeArray[j];
        const distance = calculateDistance(node1.coordinates, node2.coordinates);
        
        // Connect nodes that are very close (intersections)
        if (distance < 15) { // 15 meters threshold for intersections
          if (!graph.edges.has(node1.id)) {
            graph.edges.set(node1.id, new Map());
          }
          if (!graph.edges.has(node2.id)) {
            graph.edges.set(node2.id, new Map());
          }
          
          graph.edges.get(node1.id).set(node2.id, distance);
          graph.edges.get(node2.id).set(node1.id, distance);
        }
      }
    }
    
    console.log('Road graph built:', {
      nodes: graph.nodes.size,
      edges: Array.from(graph.edges.values()).reduce((sum, edges) => sum + edges.size, 0) / 2
    });
    
    return graph;
  };

  // Find the nearest road point to a grave
  const findNearestRoadPointToGrave = (graveCoordinate, roadGraph) => {
    let nearestNode = null;
    let minDistance = Infinity;
    
    roadGraph.nodes.forEach((node, nodeId) => {
      const distance = calculateDistance(graveCoordinate, node.coordinates);
      if (distance < minDistance) {
        minDistance = distance;
        nearestNode = node.coordinates;
      }
    });
    
    console.log('Nearest road point to grave:', nearestNode, 'at distance:', minDistance);
    return nearestNode;
  };

  // Find shortest path through roads using Dijkstra's algorithm
  const findShortestPathThroughRoads = (startCoord, endCoord, roadGraph) => {
    // Find start and end nodes
    let startNode = null;
    let endNode = null;
    let minStartDistance = Infinity;
    let minEndDistance = Infinity;
    
    roadGraph.nodes.forEach((node, nodeId) => {
      const startDist = calculateDistance(startCoord, node.coordinates);
      const endDist = calculateDistance(endCoord, node.coordinates);
      
      if (startDist < minStartDistance) {
        minStartDistance = startDist;
        startNode = nodeId;
      }
      if (endDist < minEndDistance) {
        minEndDistance = endDist;
        endNode = nodeId;
      }
    });
    
    if (!startNode || !endNode) {
      console.warn('Could not find start or end nodes in road graph');
      return [];
    }
    
    console.log('Finding path from', startNode, 'to', endNode);
    
    // Use Dijkstra's algorithm
    const distances = new Map();
    const previous = new Map();
    const unvisited = new Set();
    
    // Initialize distances
    roadGraph.nodes.forEach((node, nodeId) => {
      distances.set(nodeId, nodeId === startNode ? 0 : Infinity);
      unvisited.add(nodeId);
    });
    
    while (unvisited.size > 0) {
      // Find unvisited node with smallest distance
      let currentNode = null;
      let minDistance = Infinity;
      
      unvisited.forEach(nodeId => {
        const distance = distances.get(nodeId);
        if (distance < minDistance) {
          minDistance = distance;
          currentNode = nodeId;
        }
      });
      
      if (currentNode === null || currentNode === endNode) {
        break;
      }
      
      unvisited.delete(currentNode);
      
      // Check neighbors
      const neighbors = roadGraph.edges.get(currentNode);
      if (neighbors) {
        neighbors.forEach((edgeDistance, neighborId) => {
          if (unvisited.has(neighborId)) {
            const newDistance = distances.get(currentNode) + edgeDistance;
            if (newDistance < distances.get(neighborId)) {
              distances.set(neighborId, newDistance);
              previous.set(neighborId, currentNode);
            }
          }
        });
      }
    }
    
    // Reconstruct path
    const path = [];
    let currentNode = endNode;
    
    while (currentNode !== undefined) {
      path.unshift(roadGraph.nodes.get(currentNode).coordinates);
      currentNode = previous.get(currentNode);
    }
    
    console.log('Shortest road path found with', path.length, 'points');
    return path;
  };

  // Build a proper road graph from GeoJSON data with proper road stitching
  const buildRoadGraph = (geoJsonData) => {
    const graph = {
      nodes: new Map(),
      edges: new Map()
    };
    
    // First pass: Collect all road coordinates and create intersection points
    const allRoadCoords = [];
    const roadFeatures = geoJsonData.features.filter(feature => 
      feature.properties.type === 'main' || feature.properties.type === 'pathway'
    );
    
    console.log('Processing road features:', roadFeatures.length);
    
    // Process each road feature and collect all coordinates
    roadFeatures.forEach((feature, featureIndex) => {
      if (feature.geometry.coordinates) {
        const coords = feature.geometry.coordinates;
        coords.forEach((coord, coordIndex) => {
          allRoadCoords.push({
            coord: coord,
            featureIndex: featureIndex,
            coordIndex: coordIndex,
            roadName: feature.properties.name || `Road-${featureIndex}`,
            roadType: feature.properties.type
          });
        });
      }
    });
    
    console.log('Total road coordinates collected:', allRoadCoords.length);
    
    // Second pass: Create nodes and find intersections
    const intersectionThreshold = 0.0001; // ~10 meters
    const coordToNodeId = new Map();
    
    allRoadCoords.forEach((roadCoord, index) => {
      const coord = roadCoord.coord;
      const coordKey = `${coord[0].toFixed(6)},${coord[1].toFixed(6)}`;
      
      // Check if this coordinate is close to any existing node (intersection)
      let nodeId = null;
      let isIntersection = false;
      
      for (const [existingCoordKey, existingNodeId] of coordToNodeId) {
        const existingCoord = existingNodeId.split(',');
        const distance = calculateDistance(coord, [parseFloat(existingCoord[0]), parseFloat(existingCoord[1])]);
        
        if (distance < intersectionThreshold) {
          nodeId = existingNodeId;
          isIntersection = true;
          break;
        }
      }
      
      // Create new node if not an intersection
      if (!nodeId) {
        nodeId = coordKey;
        graph.nodes.set(nodeId, {
          id: nodeId,
          coordinates: coord,
          roads: new Set(),
          isIntersection: false
        });
      }
      
      // Add road information to node
      graph.nodes.get(nodeId).roads.add(roadCoord.featureIndex);
      if (isIntersection) {
        graph.nodes.get(nodeId).isIntersection = true;
      }
      
      coordToNodeId.set(coordKey, nodeId);
    });
    
    console.log('Nodes created:', graph.nodes.size);
    console.log('Intersections found:', Array.from(graph.nodes.values()).filter(n => n.isIntersection).length);
    
    // Third pass: Create edges between consecutive points on the same road
    roadFeatures.forEach((feature, featureIndex) => {
      if (feature.geometry.coordinates) {
        const coords = feature.geometry.coordinates;
        
        for (let i = 0; i < coords.length - 1; i++) {
          const coord1 = coords[i];
          const coord2 = coords[i + 1];
          const coordKey1 = `${coord1[0].toFixed(6)},${coord1[1].toFixed(6)}`;
          const coordKey2 = `${coord2[0].toFixed(6)},${coord2[1].toFixed(6)}`;
          
          const nodeId1 = coordToNodeId.get(coordKey1);
          const nodeId2 = coordToNodeId.get(coordKey2);
          
          if (nodeId1 && nodeId2 && nodeId1 !== nodeId2) {
            const distance = calculateDistance(coord1, coord2);
            
            // Add edge from node1 to node2
            if (!graph.edges.has(nodeId1)) {
              graph.edges.set(nodeId1, new Map());
            }
            graph.edges.get(nodeId1).set(nodeId2, distance);
            
            // Add edge from node2 to node1 (bidirectional)
            if (!graph.edges.has(nodeId2)) {
              graph.edges.set(nodeId2, new Map());
            }
            graph.edges.get(nodeId2).set(nodeId1, distance);
          }
        }
      }
    });
    
    // Fourth pass: Connect nearby nodes that should be connected (within 20 meters)
    const nodeArray = Array.from(graph.nodes.values());
    for (let i = 0; i < nodeArray.length; i++) {
      for (let j = i + 1; j < nodeArray.length; j++) {
        const node1 = nodeArray[i];
        const node2 = nodeArray[j];
        const distance = calculateDistance(node1.coordinates, node2.coordinates);
        
        // Connect nodes that are very close (likely missed intersections)
        if (distance < 20) {
          if (!graph.edges.has(node1.id)) {
            graph.edges.set(node1.id, new Map());
          }
          if (!graph.edges.has(node2.id)) {
            graph.edges.set(node2.id, new Map());
          }
          
          // Only add if not already connected
          if (!graph.edges.get(node1.id).has(node2.id)) {
            graph.edges.get(node1.id).set(node2.id, distance);
            graph.edges.get(node2.id).set(node1.id, distance);
          }
        }
      }
    }
    
    const totalEdges = Array.from(graph.edges.values()).reduce((sum, edges) => sum + edges.size, 0) / 2;
    console.log('Road graph built with proper stitching:', {
      nodes: graph.nodes.size,
      edges: totalEdges,
      intersections: Array.from(graph.nodes.values()).filter(n => n.isIntersection).length
    });
    
    return graph;
  };

  // Find the nearest road node to a given coordinate
  const findNearestRoadNode = (coordinate, roadGraph) => {
    let nearestNode = null;
    let minDistance = Infinity;
    
    roadGraph.nodes.forEach((node, nodeId) => {
      const distance = calculateDistance(coordinate, node.coordinates);
      if (distance < minDistance) {
        minDistance = distance;
        nearestNode = nodeId;
      }
    });
    
    console.log('Nearest road node to coordinate:', coordinate, 'is:', nearestNode, 'at distance:', minDistance);
    return nearestNode;
  };

  // Check if a coordinate is inside a garden polygon (to avoid cutting through gardens)
  const isInsideGarden = (coordinate, geoJsonData) => {
    const gardens = (geoJsonData.features || []).filter(feature => feature && feature.properties && feature.properties.type === 'garden');
    
    for (const garden of gardens) {
      if (garden.geometry.coordinates && garden.geometry.coordinates[0]) {
        const polygon = garden.geometry.coordinates[0];
        if (pointInPolygon(coordinate, polygon)) {
          return true;
        }
      }
    }
    return false;
  };

  // Point-in-polygon test using ray casting algorithm
  const pointInPolygon = (point, polygon) => {
    const x = point[0];
    const y = point[1];
    let inside = false;
    
    for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
      const xi = polygon[i][0];
      const yi = polygon[i][1];
      const xj = polygon[j][0];
      const yj = polygon[j][1];
      
      if (((yi > y) !== (yj > y)) && (x < (xj - xi) * (y - yi) / (yj - yi) + xi)) {
        inside = !inside;
      }
    }
    
    return inside;
  };


  // Find a route that follows the perimeter of gardens instead of cutting through them
  const findPerimeterRoute = (start, end, roadGraph, geoJsonData) => {
    console.log('Finding perimeter route to avoid cutting through gardens');
    
    // Get all road nodes that are NOT inside gardens
    const validNodes = new Map();
    roadGraph.nodes.forEach((node, nodeId) => {
      if (!isInsideGarden(node.coordinates, geoJsonData)) {
        validNodes.set(nodeId, node);
      }
    });
    
    console.log('Valid road nodes (not in gardens):', validNodes.size, 'out of', roadGraph.nodes.size);
    
    if (validNodes.size === 0) {
      console.warn('No valid road nodes found outside gardens');
      return [];
    }
    
    // Find nearest valid nodes to start and end
    let startNode = null;
    let endNode = null;
    let minStartDistance = Infinity;
    let minEndDistance = Infinity;
    
    validNodes.forEach((node, nodeId) => {
      const startDist = calculateDistance(start, node.coordinates);
      const endDist = calculateDistance(end, node.coordinates);
      
      if (startDist < minStartDistance) {
        minStartDistance = startDist;
        startNode = nodeId;
      }
      if (endDist < minEndDistance) {
        minEndDistance = endDist;
        endNode = nodeId;
      }
    });
    
    if (!startNode || !endNode) {
      console.warn('Could not find valid start or end nodes');
      return [];
    }
    
    console.log('Using perimeter route from', startNode, 'to', endNode);
    
    // Use Dijkstra's algorithm on valid nodes only
    const perimeterPath = findShortestPathOnValidNodes(startNode, endNode, roadGraph, validNodes);
    
    if (perimeterPath.length > 0) {
      return perimeterPath.map(nodeId => roadGraph.nodes.get(nodeId).coordinates);
    }
    
    return [];
  };

  // Modified Dijkstra's algorithm that only uses valid nodes (not in gardens)
  const findShortestPathOnValidNodes = (startNode, endNode, roadGraph, validNodes) => {
    const distances = new Map();
    const previous = new Map();
    const unvisited = new Set();
    
    // Initialize distances for valid nodes only
    validNodes.forEach((node, nodeId) => {
      distances.set(nodeId, nodeId === startNode ? 0 : Infinity);
      unvisited.add(nodeId);
    });
    
    while (unvisited.size > 0) {
      // Find unvisited node with smallest distance
      let currentNode = null;
      let minDistance = Infinity;
      
      unvisited.forEach(nodeId => {
        const distance = distances.get(nodeId);
        if (distance < minDistance) {
          minDistance = distance;
          currentNode = nodeId;
        }
      });
      
      if (currentNode === null || currentNode === endNode) {
        break;
      }
      
      unvisited.delete(currentNode);
      
      // Check neighbors (only if they're valid nodes)
      const neighbors = roadGraph.edges.get(currentNode);
      if (neighbors) {
        neighbors.forEach((edgeDistance, neighborId) => {
          if (unvisited.has(neighborId) && validNodes.has(neighborId)) {
            const newDistance = distances.get(currentNode) + edgeDistance;
            if (newDistance < distances.get(neighborId)) {
              distances.set(neighborId, newDistance);
              previous.set(neighborId, currentNode);
            }
          }
        });
      }
    }
    
    // Reconstruct path
    const path = [];
    let currentNode = endNode;
    
    while (currentNode !== undefined) {
      path.unshift(currentNode);
      currentNode = previous.get(currentNode);
    }
    
    return path;
  };

  // Navigate between two graves
  const navigateBetweenGravesLocal = (startGrave, endGrave) => {
    if (!startGrave || !endGrave || !roadNetwork) {
      console.error('Invalid grave data or road network for navigation');
      return;
    }

    // Handle Garden A lots (from GeoJSON conversion)
    let startCoords, endCoords;
    
    if (startGrave.garden === 'A' && startGrave.isFromGeoJSON) {
      if (startGrave.coordinates && startGrave.coordinates.coordinates) {
        startCoords = startGrave.coordinates.coordinates;
      } else if (startGrave.bounds) {
        startCoords = [
      (startGrave.bounds[0][1] + startGrave.bounds[1][1]) / 2,
      (startGrave.bounds[0][0] + startGrave.bounds[1][0]) / 2
    ];
      } else {
        console.error('Start grave missing coordinates:', startGrave);
        return;
      }
    } else if (startGrave.bounds) {
      startCoords = [
        (startGrave.bounds[0][1] + startGrave.bounds[1][1]) / 2,
        (startGrave.bounds[0][0] + startGrave.bounds[1][0]) / 2
      ];
    } else {
      console.error('Invalid start grave data for navigation:', startGrave);
      return;
    }

    if (endGrave.garden === 'A' && endGrave.isFromGeoJSON) {
      if (endGrave.coordinates && endGrave.coordinates.coordinates) {
        endCoords = endGrave.coordinates.coordinates;
      } else if (endGrave.bounds) {
        endCoords = [
      (endGrave.bounds[0][1] + endGrave.bounds[1][1]) / 2,
      (endGrave.bounds[0][0] + endGrave.bounds[1][0]) / 2
    ];
      } else {
        console.error('End grave missing coordinates:', endGrave);
        return;
      }
    } else if (endGrave.bounds) {
      endCoords = [
        (endGrave.bounds[0][1] + endGrave.bounds[1][1]) / 2,
        (endGrave.bounds[0][0] + endGrave.bounds[1][0]) / 2
      ];
    } else {
      console.error('Invalid end grave data for navigation:', endGrave);
      return;
    }

    const startGraveWithCoords = { ...startGrave, coordinates: startCoords };
    const endGraveWithCoords = { ...endGrave, coordinates: endCoords };

    // Use the imported navigateBetweenGraves function from mapbox config
    const route = navigateBetweenGraves(startGraveWithCoords, endGraveWithCoords, roadNetwork);
    
    if (route) {
    setNavigationRoute(route);
    setIsNavigating(true);
    setCurrentStep(0);
      
      // Notify parent component of navigation state change
      if (onNavigationStateChange) {
        onNavigationStateChange({
          isNavigating: true,
          navigationRoute: route,
          currentStep: 0
        });
      }
    }
  };

  // Find graves within walking distance
  const findNearbyGraves = (centerPoint, maxDistance = 200) => {
    if (!roadNetwork) return [];
    
    const gravesWithCoords = lots.map(lot => ({
      ...lot,
      coordinates: [
        (lot.bounds[0][1] + lot.bounds[1][1]) / 2,
        (lot.bounds[0][0] + lot.bounds[1][0]) / 2
      ]
    }));

    return findGravesWithinDistance(centerPoint, maxDistance, gravesWithCoords, roadNetwork);
  };

  // Plan multi-grave route
  const planMultiGraveRoute = (startPoint, targetGraves) => {
    if (!roadNetwork || targetGraves.length === 0) return;

    const gravesWithCoords = targetGraves.map(grave => ({
      ...grave,
      coordinates: [
        (grave.bounds[0][1] + grave.bounds[1][1]) / 2,
        (grave.bounds[0][0] + grave.bounds[1][0]) / 2
      ]
    }));

    const optimizedRoute = optimizeMultiGraveRoute(startPoint, gravesWithCoords, roadNetwork);
    setNavigationRoute(optimizedRoute);
    setIsNavigating(true);
    setCurrentStep(0);
  };


  // Handle map click for creating new lots
  const handleMapClick = useCallback((event) => {
    if (isEditing) {
      const { lng, lat } = event.lngLat;
      setClickedPosition([lat, lng]);
      setShowCreateModal(true);
      onMapClick([lat, lng]);
    }
  }, [isEditing, onMapClick]);

  // Handle lot marker click
  const handleLotClick = useCallback((lot, event) => {
    if (event && event.stopPropagation) {
    event.stopPropagation();
    }
    setPopupInfo(lot);
    onLotClick(lot);
  }, [onLotClick]);

  // Close popup when clicking elsewhere
  const handleMapClickClose = useCallback(() => {
    setPopupInfo(null);
  }, []);

  // Get marker color based on lot status
  const getMarkerColor = (status) => {
    return MAPBOX_CONFIG.lotMarkers[status]?.color || '#6c757d';
  };

  // Get marker size based on lot type
  const getMarkerSize = (type) => {
    return type === 'landmark' ? 20 : 12;
  };

  // Render Garden A 3D graves and niches
  const renderGardenA3D = () => {
    if (!gardenAData || !isStyleLoaded) {
      return null;
    }

    // Filter graves and niches from the GeoJSON data
    const allGraves = (gardenAData?.features || []).filter(feature => feature && feature.properties && feature.properties.type === 'grave');
    const allNiches = (gardenAData?.features || []).filter(feature => feature && feature.properties && feature.properties.type === 'niche');
    
    // Apply status filter
    let graves, niches;
    if (statusFilter === 'all') {
      graves = allGraves;
      niches = allNiches;
    } else {
      // Filter graves directly by status
      graves = allGraves.filter(feature => {
        const props = feature.properties;
        const row = props.row || props.grave_row;
        const column = props.column || props.grave_column;
        const status = getRealisticStatus(props.name, row, column);
        return status === statusFilter;
      });
      
      // For niches, find matching locations
      const validGraveLocations = new Set();
      graves.forEach(feature => {
        const props = feature.properties;
        const row = props.row || props.grave_row;
        const column = props.column || props.grave_column;
        const locationKey = `${row}-${column}`;
        validGraveLocations.add(locationKey);
      });
      
      niches = allNiches.filter(feature => {
        const props = feature.properties;
        const row = props.row || props.grave_row;
        const column = props.column || props.grave_column;
        const locationKey = `${row}-${column}`;
        return validGraveLocations.has(locationKey);
      });
    }

    // console.log(`Rendering Garden A 3D: ${graves.length} graves, ${niches.length} niches`);

    return (
      <>
        {/* Garden A Graves - 3D Fill Extrusion with status-based colors */}
        {statusFilter === 'all' ? (
          // Show all statuses with different colors
          ['available', 'occupied', 'reserved', 'unavailable'].map(status => {
            const statusGraves = graves.filter(feature => {
              const props = feature.properties;
              const row = props.row || props.grave_row;
              const column = props.column || props.grave_column;
              return (props.status || 'available') === status; // Use real status from database
            });
            
            if (statusGraves.length === 0) return null;
            
            return (
              <Source
                key={`garden-a-graves-3d-${status}`}
                id={`garden-a-graves-3d-${status}`}
                type="geojson"
                data={{
                  type: 'FeatureCollection',
                  features: statusGraves
                }}
              >
                <Layer
                  id={`garden-a-graves-3d-fill-${status}`}
                  type="fill-extrusion"
                  paint={{
                    'fill-extrusion-color': getStatusColor(status),
                    'fill-extrusion-height': 0.254, // 10 inches in meters
                    'fill-extrusion-base': 0,
                    'fill-extrusion-opacity': 1.0
                  }}
                />
              </Source>
            );
          })
        ) : (
          // Show only the filtered status
          graves.length > 0 && (
            <Source
              key="garden-a-graves-3d-filtered"
              id="garden-a-graves-3d-filtered"
              type="geojson"
              data={{
                type: 'FeatureCollection',
                features: graves
              }}
            >
              <Layer
                id="garden-a-graves-3d-fill-filtered"
                type="fill-extrusion"
                paint={{
                  'fill-extrusion-color': getStatusColor(statusFilter),
                  'fill-extrusion-height': 0.254, // 10 inches in meters
                  'fill-extrusion-base': 0,
                  'fill-extrusion-opacity': 1.0
                }}
              />
            </Source>
          )
        )}

        {/* Garden A Niches - 3D Fill Extrusion (5 inches = 0.127 meters, gray) */}
        {niches.length > 0 && (
          <Source
            key="garden-a-niches-3d"
            id="garden-a-niches-3d"
            type="geojson"
            data={{
              type: 'FeatureCollection',
              features: niches
            }}
          >
            <Layer
              id="garden-a-niches-3d-fill"
              type="fill-extrusion"
              paint={{
                'fill-extrusion-color': '#6b7280', // Gray color
                'fill-extrusion-height': 0.381, // 15 inches in meters
                'fill-extrusion-base': 0,
                'fill-extrusion-opacity': 1.0
              }}
            />
          </Source>
        )}
      </>
    );
  };

  // Render Garden B 3D graves and niches
  const renderGardenB3D = () => {
    if (!gardenBData || !isStyleLoaded) {
      return null;
    }

    // Filter graves and niches from the GeoJSON data
    const allGraves = (gardenBData?.features || []).filter(feature => feature && feature.properties && feature.properties.type === 'grave');
    const allNiches = (gardenBData?.features || []).filter(feature => feature && feature.properties && feature.properties.type === 'niche');
    
    // Apply status filter
    let graves, niches;
    if (statusFilter === 'all') {
      graves = allGraves;
      niches = allNiches;
    } else {
      // Filter graves directly by status
      graves = allGraves.filter(feature => {
        const props = feature.properties;
        const row = props.row || props.grave_row;
        const column = props.column || props.grave_column;
        const status = getRealisticStatus(props.name, row, column);
        return status === statusFilter;
      });
      
      // For niches, find matching locations
      const validGraveLocations = new Set();
      graves.forEach(feature => {
        const props = feature.properties;
        const row = props.row || props.grave_row;
        const column = props.column || props.grave_column;
        const locationKey = `${row}-${column}`;
        validGraveLocations.add(locationKey);
      });
      
      niches = allNiches.filter(feature => {
        const props = feature.properties;
        const row = props.row || props.grave_row;
        const column = props.column || props.grave_column;
        const locationKey = `${row}-${column}`;
        return validGraveLocations.has(locationKey);
      });
    }

    // console.log(`Rendering Garden B 3D: ${graves.length} graves, ${niches.length} niches`);

    return (
      <>
        {/* Garden B Graves - 3D Fill Extrusion with status-based colors */}
        {statusFilter === 'all' ? (
          // Show all statuses with different colors
          ['available', 'occupied', 'reserved', 'unavailable'].map(status => {
            const statusGraves = graves.filter(feature => {
              const props = feature.properties;
              const row = props.row || props.grave_row;
              const column = props.column || props.grave_column;
              return (props.status || 'available') === status; // Use real status from database
            });
            
            if (statusGraves.length === 0) return null;
            
            return (
              <Source
                key={`garden-b-graves-3d-${status}`}
                id={`garden-b-graves-3d-${status}`}
                type="geojson"
                data={{
                  type: 'FeatureCollection',
                  features: statusGraves
                }}
              >
                <Layer
                  id={`garden-b-graves-3d-fill-${status}`}
                  type="fill-extrusion"
                  paint={{
                    'fill-extrusion-color': getStatusColor(status),
                    'fill-extrusion-height': 0.254, // 10 inches in meters
                    'fill-extrusion-base': 0,
                    'fill-extrusion-opacity': 1.0
                  }}
                />
              </Source>
            );
          })
        ) : (
          // Show only the filtered status
          graves.length > 0 && (
            <Source
              key="garden-b-graves-3d-filtered"
              id="garden-b-graves-3d-filtered"
              type="geojson"
              data={{
                type: 'FeatureCollection',
                features: graves
              }}
            >
              <Layer
                id="garden-b-graves-3d-fill-filtered"
                type="fill-extrusion"
                paint={{
                  'fill-extrusion-color': getStatusColor(statusFilter),
                  'fill-extrusion-height': 0.254, // 10 inches in meters
                  'fill-extrusion-base': 0,
                  'fill-extrusion-opacity': 1.0
                }}
              />
            </Source>
          )
        )}

        {/* Garden B Niches - 3D Fill Extrusion (5 inches = 0.127 meters, gray) */}
        {niches.length > 0 && (
          <Source
            key="garden-b-niches-3d"
            id="garden-b-niches-3d"
            type="geojson"
            data={{
              type: 'FeatureCollection',
              features: niches
            }}
          >
            <Layer
              id="garden-b-niches-3d-fill"
              type="fill-extrusion"
              paint={{
                'fill-extrusion-color': '#6b7280', // Gray color
                'fill-extrusion-height': 0.381, // 15 inches in meters
                'fill-extrusion-base': 0,
                'fill-extrusion-opacity': 1.0
              }}
            />
          </Source>
        )}
      </>
    );
  };

  // Render Garden C 3D graves and niches
  const renderGardenC3D = () => {
    if (!gardenCData || !isStyleLoaded) {
      return null;
    }

    // Filter graves and niches from the GeoJSON data
    const allGraves = (gardenCData?.features || []).filter(feature => feature && feature.properties && feature.properties.type === 'grave');
    const allNiches = (gardenCData?.features || []).filter(feature => feature && feature.properties && feature.properties.type === 'niche');
    
    // Apply status filter
    let graves, niches;
    if (statusFilter === 'all') {
      graves = allGraves;
      niches = allNiches;
    } else {
      // Filter graves directly by status
      graves = allGraves.filter(feature => {
        const props = feature.properties;
        const row = props.row || props.grave_row;
        const column = props.column || props.grave_column;
        const status = getRealisticStatus(props.name, row, column);
        return status === statusFilter;
      });
      
      // For niches, find matching locations
      const validGraveLocations = new Set();
      graves.forEach(feature => {
        const props = feature.properties;
        const row = props.row || props.grave_row;
        const column = props.column || props.grave_column;
        const locationKey = `${row}-${column}`;
        validGraveLocations.add(locationKey);
      });
      
      niches = allNiches.filter(feature => {
        const props = feature.properties;
        const row = props.row || props.grave_row;
        const column = props.column || props.grave_column;
        const locationKey = `${row}-${column}`;
        return validGraveLocations.has(locationKey);
      });
    }

    // console.log(`Rendering Garden C 3D: ${graves.length} graves, ${niches.length} niches`);

    return (
      <>
        {/* Garden C Graves - 3D Fill Extrusion with status-based colors */}
        {statusFilter === 'all' ? (
          // Show all statuses with different colors
          ['available', 'occupied', 'reserved', 'unavailable'].map(status => {
            const statusGraves = graves.filter(feature => {
              const props = feature.properties;
              const row = props.row || props.grave_row;
              const column = props.column || props.grave_column;
              return (props.status || 'available') === status; // Use real status from database
            });
            
            if (statusGraves.length === 0) return null;
            
            return (
              <Source
                key={`garden-c-graves-3d-${status}`}
                id={`garden-c-graves-3d-${status}`}
                type="geojson"
                data={{
                  type: 'FeatureCollection',
                  features: statusGraves
                }}
              >
                <Layer
                  id={`garden-c-graves-3d-fill-${status}`}
                  type="fill-extrusion"
                  paint={{
                    'fill-extrusion-color': getStatusColor(status),
                    'fill-extrusion-height': 0.254, // 10 inches in meters
                    'fill-extrusion-base': 0,
                    'fill-extrusion-opacity': 1.0
                  }}
                />
              </Source>
            );
          })
        ) : (
          // Show only the filtered status
          graves.length > 0 && (
            <Source
              key="garden-c-graves-3d-filtered"
              id="garden-c-graves-3d-filtered"
              type="geojson"
              data={{
                type: 'FeatureCollection',
                features: graves
              }}
            >
              <Layer
                id="garden-c-graves-3d-fill-filtered"
                type="fill-extrusion"
                paint={{
                  'fill-extrusion-color': getStatusColor(statusFilter),
                  'fill-extrusion-height': 0.254, // 10 inches in meters
                  'fill-extrusion-base': 0,
                  'fill-extrusion-opacity': 1.0
                }}
              />
            </Source>
          )
        )}

        {/* Garden C Niches - 3D Fill Extrusion (5 inches = 0.127 meters, gray) */}
        {niches.length > 0 && (
          <Source
            key="garden-c-niches-3d"
            id="garden-c-niches-3d"
            type="geojson"
            data={{
              type: 'FeatureCollection',
              features: niches
            }}
          >
            <Layer
              id="garden-c-niches-3d-fill"
              type="fill-extrusion"
              paint={{
                'fill-extrusion-color': '#6b7280', // Gray color
                'fill-extrusion-height': 0.381, // 15 inches in meters
                'fill-extrusion-base': 0,
                'fill-extrusion-opacity': 1.0
              }}
            />
          </Source>
        )}
      </>
    );
  };

  // Render Garden D 3D graves and niches
  const renderGardenD3D = () => {
    if (!gardenDData || !isStyleLoaded) {
      return null;
    }

    // Filter graves and niches from the GeoJSON data
    const allGraves = (gardenDData?.features || []).filter(feature => feature && feature.properties && feature.properties.type === 'grave');
    const allNiches = (gardenDData?.features || []).filter(feature => feature && feature.properties && feature.properties.type === 'niche');
    
    // Apply status filter
    let graves, niches;
    if (statusFilter === 'all') {
      graves = allGraves;
      niches = allNiches;
    } else {
      // Filter graves directly by status
      graves = allGraves.filter(feature => {
        const props = feature.properties;
        const row = props.row || props.grave_row;
        const column = props.column || props.grave_column;
        const status = getRealisticStatus(props.name, row, column);
        return status === statusFilter;
      });
      
      // For niches, find matching locations
      const validGraveLocations = new Set();
      graves.forEach(feature => {
        const props = feature.properties;
        const row = props.row || props.grave_row;
        const column = props.column || props.grave_column;
        const locationKey = `${row}-${column}`;
        validGraveLocations.add(locationKey);
      });
      
      niches = allNiches.filter(feature => {
        const props = feature.properties;
        const row = props.row || props.grave_row;
        const column = props.column || props.grave_column;
        const locationKey = `${row}-${column}`;
        return validGraveLocations.has(locationKey);
      });
    }

    // console.log(`Rendering Garden D 3D: ${graves.length} graves, ${niches.length} niches`);

    return (
      <>
        {/* Garden D Graves - 3D Fill Extrusion with status-based colors */}
        {statusFilter === 'all' ? (
          // Show all statuses with different colors
          ['available', 'occupied', 'reserved', 'unavailable'].map(status => {
            const statusGraves = graves.filter(feature => {
              const props = feature.properties;
              const row = props.row || props.grave_row;
              const column = props.column || props.grave_column;
              return (props.status || 'available') === status; // Use real status from database
            });
            
            if (statusGraves.length === 0) return null;
            
            return (
              <Source
                key={`garden-d-graves-3d-${status}`}
                id={`garden-d-graves-3d-${status}`}
                type="geojson"
                data={{
                  type: 'FeatureCollection',
                  features: statusGraves
                }}
              >
                <Layer
                  id={`garden-d-graves-3d-fill-${status}`}
                  type="fill-extrusion"
                  paint={{
                    'fill-extrusion-color': getStatusColor(status),
                    'fill-extrusion-height': 0.254, // 10 inches in meters
                    'fill-extrusion-base': 0,
                    'fill-extrusion-opacity': 1.0
                  }}
                />
              </Source>
            );
          })
        ) : (
          // Show only the filtered status
          graves.length > 0 && (
            <Source
              key="garden-d-graves-3d-filtered"
              id="garden-d-graves-3d-filtered"
              type="geojson"
              data={{
                type: 'FeatureCollection',
                features: graves
              }}
            >
              <Layer
                id="garden-d-graves-3d-fill-filtered"
                type="fill-extrusion"
                paint={{
                  'fill-extrusion-color': getStatusColor(statusFilter),
                  'fill-extrusion-height': 0.254, // 10 inches in meters
                  'fill-extrusion-base': 0,
                  'fill-extrusion-opacity': 1.0
                }}
              />
            </Source>
          )
        )}

        {/* Garden D Niches - 3D Fill Extrusion (5 inches = 0.127 meters, gray) */}
        {niches.length > 0 && (
          <Source
            key="garden-d-niches-3d"
            id="garden-d-niches-3d"
            type="geojson"
            data={{
              type: 'FeatureCollection',
              features: niches
            }}
          >
            <Layer
              id="garden-d-niches-3d-fill"
              type="fill-extrusion"
              paint={{
                'fill-extrusion-color': '#6b7280', // Gray color
                'fill-extrusion-height': 0.381, // 15 inches in meters
                'fill-extrusion-base': 0,
                'fill-extrusion-opacity': 1.0
              }}
            />
          </Source>
        )}
      </>
    );
  };

  // Render grave rectangles as polygons (DISABLED - using 3D graves instead)
  const renderGraveRectangles_DISABLED = () => {
    // console.log('renderGraveRectangles called, isStyleLoaded:', isStyleLoaded, 'lots count:', lots.length);
    
    if (!isStyleLoaded) {
      // console.log('Style not loaded yet, returning null');
      return null;
    }
    
    // console.log('renderGraveRectangles called with lots:', lots.length, lots);
    
    // Group graves by garden for better organization (exclude Garden A - now handled by 3D system)
    const gravesByGarden = lots.reduce((acc, lot) => {
      const garden = lot.garden || 'Unknown';
      // Skip Garden A - it's now handled by the 3D GeoJSON system
      if (garden === 'A') return acc;
      if (!acc[garden]) acc[garden] = [];
      acc[garden].push(lot);
      return acc;
    }, {});
    
    // console.log('gravesByGarden:', gravesByGarden);

    return Object.entries(gravesByGarden).map(([garden, graves]) => {
      // Garden polygon coordinates (exact from your GeoJSON data)
      const gardenPolygons = {
        'A': [
          [121.069415, 14.53879],
          [121.069888, 14.538776],
          [121.069876, 14.538393],
          [121.069403, 14.538394],
          [121.069415, 14.53879] // Close the polygon
        ],
        'B': [
          [121.069928, 14.538777],
          [121.070611, 14.538762],
          [121.070609, 14.538391],
          [121.069919, 14.53839],
          [121.069928, 14.538777] // Close the polygon
        ],
        'C': [
          [121.070769, 14.538362],
          [121.071787, 14.538341],
          [121.07189, 14.538103],
          [121.070762, 14.53812],
          [121.070769, 14.538362] // Close the polygon
        ]
      };

      // Point-in-polygon test using ray casting algorithm
      const pointInPolygon = (point, polygon) => {
        const [x, y] = point;
        let inside = false;
        
        for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
          const [xi, yi] = polygon[i];
          const [xj, yj] = polygon[j];
          
          if (((yi > y) !== (yj > y)) && (x < (xj - xi) * (y - yi) / (yj - yi) + xi)) {
            inside = !inside;
          }
        }
        
        return inside;
      };
      
      const polygon = gardenPolygons[garden] || gardenPolygons['A'];
      
      // Calculate garden bounds for positioning
      const minLng = Math.min(...polygon.map(p => p[0]));
      const maxLng = Math.max(...polygon.map(p => p[0]));
      const minLat = Math.min(...polygon.map(p => p[1]));
      const maxLat = Math.max(...polygon.map(p => p[1]));
      
      const bounds = { minLng, maxLng, minLat, maxLat };
      
      // Calculate garden dimensions
      const gardenWidth = bounds.maxLng - bounds.minLng;
      const gardenHeight = bounds.maxLat - bounds.minLat;
      
      // console.log(`Garden ${garden} bounds:`, bounds);
      // console.log(`Garden ${garden} dimensions: ${gardenWidth.toFixed(6)}° × ${gardenHeight.toFixed(6)}°`);
      
      // Real standard grave dimensions (1m × 2m)
      const standardGraveWidth = 0.00000465; // 1m in degrees longitude
      const standardGraveLength = 0.000009;  // 2m in degrees latitude
      
      // Calculate how many graves can fit with standard size
      const maxColumns = Math.floor(gardenWidth / standardGraveWidth);
      const maxRows = Math.floor(gardenHeight / standardGraveLength);
      
      // console.log(`Garden ${garden}: can fit ${maxRows} rows × ${maxColumns} columns with standard size`);
      // console.log(`Garden ${garden}: total capacity = ${maxRows * maxColumns} graves`);
      // console.log(`Garden ${garden}: actual graves in data: ${graves.length}`);
      
      // Find the actual range of rows and columns in the data to fill the garden
      const actualRows = Math.max(...graves.map(g => g.row || 0));
      const actualColumns = Math.max(...graves.map(g => g.column || 0));
      
      // Use the actual data range to calculate grave size (to fill the garden)
      const usedRows = Math.min(actualRows, maxRows);
      const usedColumns = Math.min(actualColumns, maxColumns);
      
      // Calculate actual grave size to fill the garden completely
      const actualGraveWidth = gardenWidth / usedColumns;
      const actualGraveLength = gardenHeight / usedRows;
      
      // Filter out graves that are outside the garden polygon AFTER calculating size
      const gravesWithinBounds = graves.filter(grave => {
        const row = grave.row;
        const column = grave.column;
        
        if (!row || !column) return false;
        
        // Calculate grave center position
        const offsetLng = (column - 1) * actualGraveWidth;
        const offsetLat = (row - 1) * actualGraveLength;
        
        const graveCenterLng = bounds.minLng + offsetLng + (actualGraveWidth / 2);
        const graveCenterLat = bounds.minLat + offsetLat + (actualGraveLength / 2);
        
        // Check if the grave center is within the garden polygon
        const isWithinPolygon = pointInPolygon([graveCenterLng, graveCenterLat], polygon);
        
        if (!isWithinPolygon) {
          console.warn(`Grave ${grave.id} (row ${row}, col ${column}) is outside garden ${garden} polygon - HIDING`);
        }
        
        return isWithinPolygon;
      });
      
      // console.log(`Garden ${garden}: ${graves.length} total graves, ${gravesWithinBounds.length} within bounds`);
      
      // console.log(`Garden ${garden}: actual grave size: ${actualGraveWidth} × ${actualGraveLength}`);
      // console.log(`Garden ${garden}: standard size: ${standardGraveWidth} × ${standardGraveLength}`);
      // console.log(`Garden ${garden}: size ratio: ${(actualGraveWidth/standardGraveWidth).toFixed(2)} × ${(actualGraveLength/standardGraveLength).toFixed(2)}`);
      
      return (
        <Source
          key={`graves-${garden}`}
          id={`graves-${garden}`}
          type="geojson"
          data={{
            type: 'FeatureCollection',
            features: gravesWithinBounds.map(lot => {
              // Debug: log the actual grave data structure (disabled to reduce console spam)
              // console.log(`Grave ${lot.id} data:`, lot);
              
              // Extract row and column from grave ID if not in properties
              let row, column;
              if (lot.row && lot.column) {
                row = lot.row;
                column = lot.column;
              } else if (lot.id && lot.id.match(/^[ABC]-\d+-\d+$/)) {
                // Parse from ID format: A-15-23 (Garden-Row-Column)
                const parts = lot.id.split('-');
                row = parseInt(parts[1]);
                column = parseInt(parts[2]);
                // console.log(`Parsed from ID ${lot.id}: row=${row}, column=${column}`);
              } else {
                console.warn(`Grave ${lot.id} missing row or column:`, lot.row, lot.column);
                return null;
              }
              
              // Check if we have valid coordinates
              if (!lot.coordinates || !lot.coordinates.coordinates) {
                console.warn(`Grave ${lot.id} missing coordinates:`, lot.coordinates);
                return null;
              }
              
              // Calculate position for rendering (graves are already filtered for bounds)
              const offsetLng = (column - 1) * actualGraveWidth;
              const offsetLat = (row - 1) * actualGraveLength;
              
              // Calculate the four corners of the rectangle
              const swLng = bounds.minLng + offsetLng;
              const swLat = bounds.minLat + offsetLat;
              const neLng = swLng + actualGraveWidth;
              const neLat = swLat + actualGraveLength;
              
              const rectangle = [
                [swLng, swLat], // southwest
                [neLng, swLat], // southeast  
                [neLng, neLat], // northeast
                [swLng, neLat], // northwest
                [swLng, swLat]  // close polygon
              ];
              
              return {
                type: 'Feature',
                properties: {
                  id: lot._id || lot.id,
                  graveId: lot.id,
                  status: lot.status,
                  name: lot.name || '',
                  garden: lot.garden,
                  row: row,
                  column: column,
                  type: lot.type || 'grave'
                },
                geometry: {
                  type: 'Polygon',
                  coordinates: [rectangle]
                }
              };
            }).filter(Boolean)
          }}
        >
          {/* Grave fill layer */}
          <Layer
            id={`graves-${garden}-fill`}
            type="fill"
            paint={{
              'fill-color': [
                'case',
                ['==', ['get', 'status'], 'available'], '#34c759',
                ['==', ['get', 'status'], 'occupied'], '#8e8e93', 
                ['==', ['get', 'status'], 'reserved'], '#007bff',
                ['==', ['get', 'status'], 'maintenance'], '#ff9500',
                ['==', ['get', 'status'], 'unavailable'], '#ff3b30',
                '#6c757d' // default
              ],
              'fill-opacity': 0.8
            }}
          />
          
          {/* Grave outline layer */}
          <Layer
            id={`graves-${garden}-outline`}
            type="line"
            paint={{
              'line-color': '#ffffff',
              'line-width': 1,
              'line-opacity': 0.9
            }}
          />
          
          {/* Grave labels - disabled for now to focus on positioning
          <Layer
            id={`graves-${garden}-labels`}
            type="symbol"
            layout={{
              'text-field': ['get', 'graveId'],
              'text-font': ['Open Sans Regular', 'Arial Unicode MS Regular'],
              'text-size': 10,
              'text-anchor': 'center',
              'text-allow-overlap': false,
              'text-ignore-placement': false
            }}
            paint={{
              'text-color': '#ffffff',
              'text-halo-color': '#000000',
              'text-halo-width': 1
            }}
          />
          */}
        </Source>
      );
    });
  };

  // Render landmark markers (keep circular for landmarks)
  const renderLandmarkMarkers = () => {
    return lots
      .filter(lot => lot.type === 'landmark')
      .map((lot) => {
      if (!lot.bounds || lot.bounds.length < 2) return null;
      
      // Calculate center point from bounds
      const centerLat = (lot.bounds[0][0] + lot.bounds[1][0]) / 2;
      const centerLng = (lot.bounds[0][1] + lot.bounds[1][1]) / 2;
      
      // Validate coordinates
      if (centerLat < -90 || centerLat > 90 || centerLng < -180 || centerLng > 180) {
          console.error('Invalid coordinates for landmark:', lot.id, 'lat:', centerLat, 'lng:', centerLng);
        return null;
      }
      
      return (
        <Marker
          key={lot._id || lot.id}
          latitude={centerLat}
          longitude={centerLng}
          onClick={(e) => handleLotClick(lot, e)}
        >
          <div style={{ position: 'relative', width: 40, height: 40 }}>
            {/* Outer pulsing circle */}
            <div
              className="landmark-circle-pulse"
              style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: 40,
                height: 40,
                borderRadius: '50%',
                backgroundColor: 'rgba(52, 152, 219, 0.3)',
                border: '2px solid #3498db',
                animation: 'pulse 2s infinite',
                cursor: 'pointer'
              }}
            />
            {/* Inner pin marker */}
            <div
              className={`landmark-marker ${lot.status}`}
              style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%) rotate(-45deg)',
                backgroundColor: '#3498db',
                width: 20,
                height: 20,
                borderRadius: '50% 50% 50% 0',
                border: '2px solid white',
                boxShadow: '0 2px 4px rgba(0,0,0,0.3)',
                cursor: 'pointer',
                zIndex: 2
              }}
              title={`${lot.name || lot.id} - ${lot.status}`}
            />
          </div>
        </Marker>
      );
    });
  };

  // Roads are now rendered by your custom Mapbox style
  // No need for manual road rendering since they're defined in your style

  // Render navigation route
  const renderNavigationRoute = () => {
    // console.log('renderNavigationRoute called:', {
    //   navigationRoute: !!navigationRoute,
    //   isNavigating,
    //   isStyleLoaded,
    //   waypoints: navigationRoute?.waypoints?.length
    // });
    
    if (!navigationRoute || !isNavigating || !isStyleLoaded) {
      // console.log('Not rendering route - missing requirements');
      return null;
    }

    if (!navigationRoute.waypoints || navigationRoute.waypoints.length < 2) {
      // console.log('Not rendering route - insufficient waypoints');
      return null;
    }

    // console.log('Rendering route with', navigationRoute.waypoints.length, 'waypoints');

    return (
      <>
        {/* Navigation Route Line */}
        <Source
          id="navigation-route"
          type="geojson"
          data={{
            type: 'Feature',
            geometry: {
              type: 'LineString',
              coordinates: navigationRoute.waypoints
            },
            properties: {}
          }}
        >
          <Layer
            id="route-line"
            type="line"
            paint={{
              'line-color': '#007bff',
              'line-width': 6,
              'line-opacity': 1.0
            }}
          />
        </Source>

        {/* Current Location Marker - "You are here" */}
        <Source
          id="current-location"
          type="geojson"
          data={{
            type: 'Feature',
            geometry: {
              type: 'Point',
              coordinates: navigationRoute.waypoints[0] // Starting point (entrance)
            },
            properties: {
              name: 'You are here'
            }
          }}
        >
          {/* Outer pulsing ring */}
          <Layer
            id="current-location-pulse-outer"
            type="circle"
            paint={{
              'circle-radius': 20,
              'circle-color': '#28a745',
              'circle-opacity': 0.3
            }}
          />
          {/* Inner pulsing ring */}
          <Layer
            id="current-location-pulse-inner"
            type="circle"
            paint={{
              'circle-radius': 15,
              'circle-color': '#28a745',
              'circle-opacity': 0.5
            }}
          />
          {/* Main circle */}
          <Layer
            id="current-location-circle"
            type="circle"
            paint={{
              'circle-radius': 10,
              'circle-color': '#28a745',
              'circle-stroke-color': '#ffffff',
              'circle-stroke-width': 3,
              'circle-opacity': 1.0
            }}
          />
          {/* Inner dot */}
          <Layer
            id="current-location-dot"
            type="circle"
            paint={{
              'circle-radius': 4,
              'circle-color': '#ffffff',
              'circle-opacity': 1.0
            }}
          />
        </Source>

        {/* Current Location Text Label */}
        <Source
          id="current-location-label"
          type="geojson"
          data={{
            type: 'Feature',
            geometry: {
              type: 'Point',
              coordinates: navigationRoute.waypoints[0]
            },
            properties: {
              name: 'You are here'
            }
          }}
        >
          <Layer
            id="current-location-text"
            type="symbol"
            layout={{
              'text-field': 'You are here',
              'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold'],
              'text-size': 14,
              'text-anchor': 'top',
              'text-offset': [0, 2],
              'text-allow-overlap': true
            }}
            paint={{
              'text-color': '#28a745',
              'text-halo-color': '#ffffff',
              'text-halo-width': 2
            }}
          />
        </Source>

        {/* Destination Marker - "Destination" */}
        <Source
          id="destination-location"
          type="geojson"
          data={{
            type: 'Feature',
            geometry: {
              type: 'Point',
              coordinates: navigationRoute.waypoints[navigationRoute.waypoints.length - 1] // Destination point
            },
            properties: {
              name: 'Destination'
            }
          }}
        >
          {/* Outer pulsing ring */}
          <Layer
            id="destination-location-pulse-outer"
            type="circle"
            paint={{
              'circle-radius': 20,
              'circle-color': '#dc3545',
              'circle-opacity': 0.3
            }}
          />
          {/* Inner pulsing ring */}
          <Layer
            id="destination-location-pulse-inner"
            type="circle"
            paint={{
              'circle-radius': 15,
              'circle-color': '#dc3545',
              'circle-opacity': 0.5
            }}
          />
          {/* Main circle */}
          <Layer
            id="destination-location-circle"
            type="circle"
            paint={{
              'circle-radius': 10,
              'circle-color': '#dc3545',
              'circle-stroke-color': '#ffffff',
              'circle-stroke-width': 3,
              'circle-opacity': 1.0
            }}
          />
          {/* Inner dot */}
          <Layer
            id="destination-location-dot"
            type="circle"
            paint={{
              'circle-radius': 4,
              'circle-color': '#ffffff',
              'circle-opacity': 1.0
            }}
          />
        </Source>

        {/* Destination Location Text Label */}
        <Source
          id="destination-location-label"
          type="geojson"
          data={{
            type: 'Feature',
            geometry: {
              type: 'Point',
              coordinates: navigationRoute.waypoints[navigationRoute.waypoints.length - 1]
            },
            properties: {
              name: 'Destination'
            }
          }}
        >
          <Layer
            id="destination-location-text"
            type="symbol"
            layout={{
              'text-field': '🎯 Destination',
              'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold'],
              'text-size': 14,
              'text-anchor': 'top',
              'text-offset': [0, 2],
              'text-allow-overlap': true
            }}
            paint={{
              'text-color': '#dc3545',
              'text-halo-color': '#ffffff',
              'text-halo-width': 2
            }}
          />
        </Source>
      </>
    );
  };

  // Render columbarium building
  const renderColumbarium = () => {
    if (!showColumbarium || !isStyleLoaded) return null;

    const columbariumBounds = [
      [14.5378, 121.0691], // Top-left
      [14.5379, 121.0691], // Top-right
      [14.5379, 121.0693], // Bottom-right
      [14.5378, 121.0693], // Bottom-left
      [14.5378, 121.0691]  // Close polygon
    ];

    return (
      <Source
        id="columbarium"
        type="geojson"
        data={{
          type: 'FeatureCollection',
          features: [{
            type: 'Feature',
            properties: { name: 'Columbarium' },
            geometry: {
              type: 'Polygon',
              coordinates: [columbariumBounds]
            }
          }]
        }}
      >
        <Layer
          id="columbarium-fill"
          type="fill"
          paint={{
            'fill-color': '#2c3e50',
            'fill-opacity': 0.7
          }}
        />
        <Layer
          id="columbarium-outline"
          type="line"
          paint={{
            'line-color': '#34495e',
            'line-width': 2
          }}
        />
      </Source>
    );
  };

  // Show error if map failed to load
  if (mapError) {
    return (
      <div className={`mapbox-container error ${className}`} style={style}>
        <div style={{ textAlign: 'center', padding: '20px' }}>
          <h3>Map Error</h3>
          <p>{mapError}</p>
          <button onClick={() => setMapError(null)}>Retry</button>
        </div>
      </div>
    );
  }

  // Show columbarium grid if enabled
  if (showColumbariumGrid) {
    return (
        <ColumbariumGrid 
          onBackToMap={() => setShowColumbariumGrid(false)}
          statusFilter={statusFilter}
          onFloorChange={setCurrentColumbariumFloor}
          onFilterChange={onStatusFilterChange}
        />
    );
  }

  return (
    <div className={`mapbox-container ${className}`} style={style}>
      <Map
        ref={mapRef}
        {...viewport}
        onMove={evt => setViewport(evt.viewState)}
        onLoad={(evt) => {
          // Map loaded successfully
          console.log('Mapbox map loaded');
          setMapError(null);
          
          const map = evt.target;
          
          // Wait for style to be fully loaded
          if (map.isStyleLoaded()) {
            setIsStyleLoaded(true);
            hideRoadLayers(map); // Hide roads, keep landmarks
          } else {
            map.on('styledata', () => {
              if (map.isStyleLoaded()) {
                setIsStyleLoaded(true);
                hideRoadLayers(map); // Hide roads, keep landmarks
              }
            });
          }
        }}
        onError={(error) => {
          console.error('Mapbox error:', error);
          setMapError(error.message || 'Map failed to load');
        }}
        mapboxAccessToken={MAPBOX_CONFIG.accessToken}
        style={{ width: '100%', height: '100%' }}
        mapStyle={currentMapStyle}
        interactiveLayerIds={[
          ...lots.reduce((acc, lot) => {
            const garden = lot.garden || 'Unknown';
            acc.push(`graves-${garden}-fill`);
            acc.push(`graves-${garden}-default-fill`);
            return acc;
          }, []),
          // 3D garden layer IDs (with and without status suffixes)
          'garden-a-graves-3d-fill',
          'garden-a-niches-3d-fill',
          'garden-b-graves-3d-fill',
          'garden-b-niches-3d-fill',
          'garden-c-graves-3d-fill',
          'garden-c-niches-3d-fill',
          'garden-d-graves-3d-fill',
          'garden-d-niches-3d-fill',
          // Filtered layer IDs (when a specific status filter is active)
          'garden-a-graves-3d-fill-filtered',
          'garden-b-graves-3d-fill-filtered',
          'garden-c-graves-3d-fill-filtered',
          'garden-d-graves-3d-fill-filtered',
          // Status-filtered layer IDs (when showing all with different colors)
          ...['available', 'occupied', 'reserved', 'unavailable'].flatMap(status => [
            `garden-a-graves-3d-fill-${status}`,
            `garden-b-graves-3d-fill-${status}`,
            `garden-c-graves-3d-fill-${status}`,
            `garden-d-graves-3d-fill-${status}`
          ])
        ]}
        onClick={(event) => {
          // Handle grave rectangle clicks
          if (event.features && event.features.length > 0) {
            const feature = event.features[0];
            
            // Handle Garden A 3D features (all layer variations)
            if (feature.layer.id.startsWith('garden-a-graves-3d-fill') || feature.layer.id.startsWith('garden-a-niches-3d-fill')) {
              const props = feature.properties;
              const row = props.row || props.grave_row;
              const column = props.column || props.grave_column;
              // Find the corresponding lot data from the Garden A lots
              const lot = gardenALots.find(l => l.garden === 'A' && l.isFromGeoJSON && 
                (l.id === props.name || l.id === props.id || l.id === `A-${row}-${column}` || l.name === props.name ||
                 (l.row === row && l.column === column)));
              
              if (lot) {
                handleLotClick(lot, event);
                return;
              }
            }
            
            // Handle Garden B 3D features (all layer variations)
            if (feature.layer.id.startsWith('garden-b-graves-3d-fill') || feature.layer.id.startsWith('garden-b-niches-3d-fill')) {
              const props = feature.properties;
              const row = props.row || props.grave_row;
              const column = props.column || props.grave_column;
              // Find the corresponding lot data from the Garden B lots
              const lot = gardenBLots.find(l => l.garden === 'B' && l.isFromGeoJSON && 
                (l.id === props.name || l.id === props.id || l.id === `B-${row}-${column}` || l.name === props.name ||
                 (l.row === row && l.column === column)));
              
              if (lot) {
                handleLotClick(lot, event);
                return;
              }
            }
            
            // Handle Garden C 3D features (all layer variations)
            if (feature.layer.id.startsWith('garden-c-graves-3d-fill') || feature.layer.id.startsWith('garden-c-niches-3d-fill')) {
              const props = feature.properties;
              const row = props.row || props.grave_row;
              const column = props.column || props.grave_column;
              // Find the corresponding lot data from the Garden C lots
              const lot = gardenCLots.find(l => l.garden === 'C' && l.isFromGeoJSON && 
                (l.id === props.name || l.id === props.id || l.id === `C-${row}-${column}` || l.name === props.name ||
                 (l.row === row && l.column === column)));
              
              if (lot) {
                handleLotClick(lot, event);
                return;
              }
            }
            
            // Handle Garden D 3D features (all layer variations)
            if (feature.layer.id.startsWith('garden-d-graves-3d-fill') || feature.layer.id.startsWith('garden-d-niches-3d-fill')) {
              const props = feature.properties;
              const row = props.row || props.grave_row;
              const column = props.column || props.grave_column;
              // Find the corresponding lot data from the Garden D lots
              const lot = gardenDLots.find(l => l.garden === 'D' && l.isFromGeoJSON && 
                (l.id === props.name || l.id === props.id || l.id === `D-${row}-${column}` || l.name === props.name ||
                 (l.row === row && l.column === column)));
              
              if (lot) {
                handleLotClick(lot, event);
                return;
              }
            }
            
            if (feature.properties && feature.properties.graveId) {
              // Find the corresponding lot data
              const lot = lots.find(l => l.id === feature.properties.graveId);
              if (lot) {
                handleLotClick(lot, event);
                return;
              }
            }
          }
          
          // Handle map clicks for editing
          handleMapClick(event);
        }}
      >
        {/* FREE FEATURES: Map Controls */}
        {MAPBOX_CONFIG.controls.navigation && (
          <NavigationControl position="top-left" />
        )}
        
        
        {MAPBOX_CONFIG.controls.fullscreen && (
          <FullscreenControl position="top-right" />
        )}
        
        {MAPBOX_CONFIG.controls.geolocate && (
          <GeolocateControl
            position="top-right"
            trackUserLocation={true}
            showAccuracyCircle={false}
            showUserHeading={true}
          />
        )}
        
        
        {/* Cemetery image overlay would go here if needed */}
        
                {/* Roads are rendered by your custom Mapbox style */}

                {/* Columbarium building */}
                {renderColumbarium()}

                {/* Navigation Route */}
                {renderNavigationRoute()}
        
        {/* Garden A 3D Graves and Niches */}
        {renderGardenA3D()}
        
        {/* Garden B 3D Graves and Niches */}
        {renderGardenB3D()}
        
        {/* Garden C 3D Graves and Niches */}
        {renderGardenC3D()}
        
        {/* Garden D 3D Graves and Niches */}
        {renderGardenD3D()}
        
        
        {/* Landmark markers */}
        {renderLandmarkMarkers()}
        
        {/* Popup for lot information */}
        {popupInfo && popupInfo.bounds && (() => {
          const popupLat = (popupInfo.bounds[0][0] + popupInfo.bounds[1][0]) / 2;
          const popupLng = (popupInfo.bounds[0][1] + popupInfo.bounds[1][1]) / 2;
          
          // Validate popup coordinates
          if (popupLat < -90 || popupLat > 90 || popupLng < -180 || popupLng > 180) {
            console.error('Invalid popup coordinates:', popupLat, popupLng);
            return null;
          }
          
          return (
             <Popup
               latitude={popupLat}
               longitude={popupLng}
               onClose={() => setPopupInfo(null)}
               closeButton={true}
               closeOnClick={false}
               anchor="bottom"
               maxWidth="250px"
             >
               <div className="lot-popup">
                 {popupInfo.type === 'landmark' ? (
                   // Landmark Popup
                   <>
                     <h4>{popupInfo.name || popupInfo.id}</h4>
                     <p><strong>Type:</strong> Landmark</p>
                     <p><strong>Category:</strong> {popupInfo.category || 'Facility'}</p>
                     <p className="landmark-description">
                       A key facility within the cemetery grounds.
                     </p>
                     
                     <button 
                       className="navigate-to-btn"
                       onClick={() => {
                         navigateToGrave(popupInfo);
                         setPopupInfo(null); // Close popup when starting navigation
                       }}
                       style={{ marginTop: '12px' }}
                     >
                       Navigate to this Landmark
                     </button>
                     
                     {/* Add Enter Columbarium button for columbarium landmarks */}
                     {(popupInfo.id === 'columbarium' || popupInfo.name === 'Columbarium') && (
                       <button 
                         className="enter-columbarium-btn"
                         onClick={() => {
                           // Show columbarium grid view
                           setShowColumbariumGrid(true);
                           setPopupInfo(null); // Close popup
                         }}
                       >
                         Enter Columbarium
                       </button>
                     )}
                   </>
                 ) : (
                   // Grave Popup
                   (() => {
                     // Calculate the actual displayed status using getRealisticStatus
                     const displayStatus = popupInfo.row && popupInfo.column 
                       ? getRealisticStatus(popupInfo.name || '', popupInfo.row, popupInfo.column)
                       : popupInfo.status;
                     
                     return (
                       <>
                         <h4>{popupInfo.id || popupInfo.graveId}</h4>
                         <p><strong>Type:</strong> {popupInfo.type || 'grave'}</p>
                         <p><strong>Status:</strong> {displayStatus}</p>
                         <p><strong>Garden:</strong> {popupInfo.garden}</p>
                         {popupInfo.birth && <p><strong>Birth:</strong> {popupInfo.birth}</p>}
                         {popupInfo.death && <p><strong>Death:</strong> {popupInfo.death}</p>}
                         {popupInfo.sqm && <p><strong>Size:</strong> {popupInfo.sqm} sqm</p>}
                         {popupInfo.price && <p><strong>Price:</strong> ₱{parseInt(popupInfo.price).toLocaleString()}</p>}
                         
                         {/* Navigate button - show for ALL graves */}
                         <button 
                           className="navigate-to-btn"
                           onClick={() => {
                             navigateToGrave(popupInfo);
                             setPopupInfo(null); // Close popup when starting navigation
                           }}
                         >
                           Navigate to Grave
                         </button>
                       </>
                     );
                   })()
                 )}
                 
                 {/* Bookmark button - show for ALL graves AND landmarks on client map (functional for all) */}
                 {onBookmark && (
                   <button 
                     className="bookmark-btn"
                     onClick={() => onBookmark(popupInfo.id || popupInfo._id)}
                     disabled={bookmarkLoading}
                   >
                     {bookmarkLoading ? '...' : bookmarks.includes(popupInfo.id || popupInfo._id) ? 'Bookmarked' : 'Bookmark'}
                   </button>
                 )}
                 
                 {/* Guest bookmark button with tooltip - show for ALL graves AND landmarks */}
                 {!onBookmark && (
                   <div style={{ position: 'relative', display: 'inline-block', width: '100%' }}>
                     <button 
                       className="bookmark-btn"
                       style={{ 
                         opacity: 0.6, 
                         cursor: 'default',
                         backgroundColor: '#f8f9fa',
                         color: '#6c757d',
                         border: '2px solid #6c757d'
                       }}
                       onMouseEnter={() => setHoveredTooltip('bookmark')}
                       onMouseLeave={() => setHoveredTooltip(null)}
                     >
                       Bookmark
                       {hoveredTooltip === 'bookmark' && (
                         <div style={{
                           position: 'absolute',
                           top: '50%',
                           right: '100%',
                           transform: 'translateY(-50%)',
                           backgroundColor: '#333',
                           color: 'white',
                           padding: '8px 12px',
                           borderRadius: '6px',
                           fontSize: '12px',
                           whiteSpace: 'nowrap',
                           zIndex: 1002,  
                           boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                           pointerEvents: 'none'
                         }}>
                           <div style={{
                             position: 'absolute',
                             top: '50%',
                             left: '100%',
                             transform: 'translateY(-50%)',
                             width: '0',
                             height: '0',
                             borderTop: '4px solid transparent',
                             borderBottom: '4px solid transparent',
                             borderLeft: '4px solid #333'
                           }}></div>
                           Need to Sign in
                         </div>
                       )}
                     </button>
                   </div>
                 )}
               </div>
             </Popup>
          );
        })()}
      </Map>
      
      
      
      {/* Navigation Panel - Removed, now handled by GraveLocator */}


    </div>
  );
});

MapboxMap.displayName = 'MapboxMap';

export default MapboxMap;
